// File: apps/trading-web/src/components/CashierDepositModal.ts
// Industry-Standard Institutional Cashier & Multi-Rail Deposit Hub for StarX Option Platform

import { RestClient } from "../api/rest_client";
import { UserProfile } from "@starx/shared-types";
import { bindDepositProof } from "../../../shared-web/deposit-proof";
import { upiPaymentURI } from "../../../shared-web/upi-payment";
import { upiEnabledFor } from "../../../shared-web/payment-eligibility";
import { generatePaymentQRCodeSVG } from "../../../shared-web/payment-qr";
import { getLiveUsdInrRate, getLiveUsdFxRate, cacheWorldFxRates } from "../../../shared-web/display-currency";
import { formatSmartBalance, getCurrencySymbol } from "../state/app_state";

export interface CashierOptions {
  restClient: RestClient;
  currentProfile: UserProfile | null;
  appState: any;
  renderHeader: () => void;
  loadFirstDepositPromotion: () => Promise<void>;
  showDashboardNotice: (message: string, type?: "info" | "success" | "warning" | "error") => void;
  showComplianceModal: (profile: any, controls?: any) => void;
  escapeHTML: (str: string) => string;
}

export interface PaymentMethodItem {
  id: string;
  name: string;
  category: "UPI" | "CRYPTO" | "FIAT";
  currency: string;
  network?: string;
  badge?: string;
  badgeColor?: string;
  speed: string;
  fee: string;
  minAmount: number;
  maxAmount: number;
  popular?: boolean;
  iconSvg: string;
  cryptoAddress?: string;
  instructions?: string;
}

// ==========================================
// PURE SVG QR CODE MATRIX GENERATOR (No CDN / External Dependencies)
// ==========================================
export function generateQRCodeSVG(text: string, size: number = 190): string {
  return generatePaymentQRCodeSVG(text, size);
}

// ==========================================
// BRAND VECTOR ICONS (Crisp High-Res SVG)
// ==========================================
export const BRAND_ICONS = {
  phonepe: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#5f259f"/><path d="M28.5 13H17.2C15.4 13 14 14.4 14 16.2v17.6c0 1.2 1 2.2 2.2 2.2h4.1V28h4.8c4.6 0 8.4-3.8 8.4-8.4 0-3.6-3-6.6-5-6.6zm-3.4 10h-4.8v-5.2h4.8c1.4 0 2.6 1.2 2.6 2.6s-1.2 2.6-2.6 2.6z" fill="#fff"/><circle cx="33" cy="33" r="3.5" fill="#00d285"/></svg>`,
  gpay: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#1e293b"/><path d="M24 20v8h8c-.7 3.4-3.5 6-8 6-4.9 0-9-4.1-9-9s4.1-9 9-9c2.3 0 4.3.9 5.8 2.3l3.6-3.6C31.1 12.6 27.7 11 24 11c-7.2 0-13 5.8-13 13s5.8 13 13 13 12.8-5.7 12.8-13c0-.9-.1-1.6-.3-2.3H24z" fill="#4285F4"/><path d="M14.5 20.4l-3.5-2.7C12.9 14.1 18.1 11 24 11l3.4 4.7c-2-.6-4.6-.3-6.9 1.4-2.3 1.7-4.1 2.2-6 3.3z" fill="#EA4335"/><path d="M11 24c0 1.2.2 2.4.7 3.5l3.8-3.1c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2l-3.8-3C11.2 19.6 11 21.8 11 24z" fill="#FBBC05"/><path d="M24 37c3.7 0 6.8-1.5 9-4.1l-3.8-3.1c-1.3 1-2.9 1.7-5.2 1.7-3.6 0-6.6-2.3-7.7-5.5l-3.8 3.1C14.7 33.3 19 37 24 37z" fill="#34A853"/></svg>`,
  paytm: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#002e6e"/><path d="M13 18h6v12h-4v-8h-2v-4zm9 0h9c1.7 0 3 1.3 3 3v2h-8v7h-4V18zm4 3h4v-1h-4v1zm7-3h6v4h-2v8h-4V18zm8 0h4v12h-4V18z" fill="#00baf2"/><circle cx="36" cy="18" r="2.5" fill="#00baf2"/></svg>`,
  bharatpe: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#0d9488"/><path d="M16 14h10c3.3 0 6 2.7 6 6 0 2.2-1.2 4.1-3 5.2 2.4.9 4 3.2 4 5.8 0 3.9-3.1 7-7 7H16V14zm5 9h5c1.1 0 2-.9 2-2s-.9-2-2-2h-5v4zm0 10h6c1.7 0 3-1.3 3-3s-1.3-3-3-3h-6v6z" fill="#ffffff"/><circle cx="35" cy="33" r="3.5" fill="#f59e0b"/></svg>`,
  bhim: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#1e293b"/><path d="M14 14h20v6H14z" fill="#ff9933"/><path d="M14 21h20v6H14z" fill="#ffffff"/><path d="M14 28h20v6H14z" fill="#138808"/><circle cx="24" cy="24" r="2.2" stroke="#000080" stroke-width="1.2" fill="none"/></svg>`,
  bank: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#334155"/><path d="M12 20h24v3H12zm2 5h4v9h-4zm7 0h4v9h-4zm7 0h4v9h-4zm7 0h4v9h-4zm-18 11h24v3H14zm10-21l14 6H10l14-6z" fill="#38bdf8"/></svg>`,
  usdt: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#26a17b"/><path d="M25.7 25.5v10.5h-3.4V25.5C16.8 25.2 13 23.8 13 22.1s3.8-3.1 9.3-3.4V14h11.4v4.7c5.5.3 9.3 1.7 9.3 3.4s-3.8 3.1-9.3 3.4zM24 23.6c4.6 0 8.1-.9 8.1-1.9s-3.5-1.9-8.1-1.9-8.1.9-8.1 1.9 3.5 1.9 8.1 1.9z" fill="#ffffff"/></svg>`,
  btc: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#f7931a"/><path d="M31.2 22.1c.4-2.8-1.7-4.3-4.6-5.3l.9-3.7-2.3-.6-.9 3.6c-.6-.1-1.2-.3-1.8-.4l.9-3.6-2.3-.6-.9 3.7c-.5-.1-1-.2-1.5-.3l-3.2-.8-.6 2.5s1.7.4 1.7.4c.9.2 1.1.8 1 1.3l-1 4.1c.1 0 .1 0 .2.1l-.2-.1-1.4 5.8c-.1.3-.4.8-1.1.6 0 0-1.7-.4-1.7-.4l-1.2 2.7 3 .8c.6.1 1.1.3 1.7.4l-.9 3.8 2.3.6.9-3.7c.6.2 1.2.3 1.8.4l-.9 3.7 2.3.6.9-3.8c4 .8 6.9.4 8.2-3.1 1-2.9-.1-4.5-2.1-5.6 1.5-.3 2.6-1.3 2.9-3.3zm-5.2 7.1c-.7 2.9-5.6 1.3-7.2.9l1.3-5.1c1.6.4 6.6 1.2 5.9 4.2zm.7-7.2c-.7 2.6-4.8 1.3-6.1.9l1.2-4.7c1.4.3 5.6 1 4.9 3.8z" fill="#ffffff"/></svg>`,
  eth: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#627eea"/><path d="M24 10l-9.5 15.8L24 31.4l9.5-5.6L24 10z" fill="#ffffff" fill-opacity=".6"/><path d="M24 10v21.4l9.5-5.6L24 10z" fill="#ffffff"/><path d="M24 33.3L14.5 27.6 24 38l9.5-10.4L24 33.3z" fill="#ffffff" fill-opacity=".6"/><path d="M24 33.3v4.7l9.5-10.4L24 33.3z" fill="#ffffff"/></svg>`,
  ton: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#0098ea"/><path d="M24 12l11 7.5L24 36 13 19.5 24 12zm0 4.2l-7.2 4.9L24 32.1l7.2-11-7.2-4.9z" fill="#ffffff"/></svg>`,
  usdc: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#2775ca"/><path d="M24 13c-6.1 0-11 4.9-11 11s4.9 11 11 11 11-4.9 11-11-4.9-11-11-11zm.6 17.5v1.5h-1.5v-1.5c-2.1-.2-3.6-1.4-3.8-3.1h2.2c.2.8 1.1 1.4 2.4 1.4 1.4 0 2.2-.6 2.2-1.5 0-1.1-.9-1.4-2.8-1.9-2.3-.6-3.8-1.4-3.8-3.3 0-1.6 1.3-2.9 3.3-3.1V17.5h1.5v1.5c1.8.2 3.1 1.3 3.4 2.8h-2.1c-.2-.7-.9-1.2-2.1-1.2-1.2 0-1.9.6-1.9 1.3 0 .9.8 1.3 2.7 1.8 2.5.6 3.9 1.5 3.9 3.4-.2 1.7-1.5 3-3.5 3.4z" fill="#ffffff"/></svg>`,
  pix: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#32bcad"/><path d="M24 14l-7 7 7 7 7-7-7-7zm-9 9l-4 4 4 4 4-4-4-4zm18 0l-4 4 4 4 4-4-4-4z" fill="#ffffff"/></svg>`,
  shield: `<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/></svg>`,
  bolt: `<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C14.9 14.66 12.96 18.06 11 21z"/></svg>`,
  tron: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><circle cx="24" cy="24" r="24" fill="#ef0027"/><path d="M11 13l26-4-14 28L11 13zm23.6-2.5L14.5 13.5l13.5 7.5 6.6-10.5zM13.8 15.2l9.7 18 2.2-10.5-11.9-7.5z" fill="#ffffff"/></svg>`,
  bsc: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><circle cx="24" cy="24" r="24" fill="#f3ba2f"/><path d="M24 13l3.5 3.5-3.5 3.5-3.5-3.5L24 13zm-6.5 6.5l3.5 3.5-3.5 3.5-3.5-3.5 3.5-3.5zm13 0l3.5 3.5-3.5 3.5-3.5-3.5 3.5-3.5zm-6.5 6.5l3.5 3.5-3.5 3.5-3.5-3.5 3.5-3.5zm-6.5 6.5l3.5 3.5-3.5 3.5-3.5-3.5 3.5-3.5zm13 0l3.5 3.5-3.5 3.5-3.5-3.5 3.5-3.5z" fill="#ffffff"/></svg>`,
  sol: `<svg viewBox="0 0 48 48" width="32" height="32" fill="none"><rect width="48" height="48" rx="12" fill="#140a2b"/><path d="M14 29.5a1.5 1.5 0 0 1 1-1.4l18.5-4a1.5 1.5 0 0 1 1.7 1.4v2.5a1.5 1.5 0 0 1-1 1.4l-18.5 4a1.5 1.5 0 0 1-1.7-1.4v-2.5zm0-9a1.5 1.5 0 0 1 1.7-1.4l18.5 4a1.5 1.5 0 0 1 1 1.4v2.5a1.5 1.5 0 0 1-1.7 1.4l-18.5-4a1.5 1.5 0 0 1-1-1.4v-2.5zm1.7-10.4a1.5 1.5 0 0 0-1.7 1.4v2.5a1.5 1.5 0 0 0 1 1.4l18.5 4a1.5 1.5 0 0 0 1.7-1.4v-2.5a1.5 1.5 0 0 0-1-1.4l-18.5-4z" fill="#00FFA3"/></svg>`,
  copy: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`,
};

export const USD_TO_INR_RATE = 95.00;

export const DEFAULT_METHODS: PaymentMethodItem[] = [
  // UPI Methods
  {
    id: "UPI_PHONEPE",
    name: "PhonePe UPI",
    category: "UPI",
    currency: "INR",
    network: "UPI",
    badge: "MOST POPULAR",
    badgeColor: "#8b5cf6",
    speed: "⚡ Instant (<3s)",
    fee: "0% Free",
    minAmount: 500,
    maxAmount: 200000,
    popular: true,
    iconSvg: BRAND_ICONS.phonepe,
  },
  {
    id: "UPI_GPAY",
    name: "Google Pay",
    category: "UPI",
    currency: "INR",
    network: "UPI",
    badge: "RECOMMENDED",
    badgeColor: "#38bdf8",
    speed: "⚡ Instant (<3s)",
    fee: "0% Free",
    minAmount: 500,
    maxAmount: 200000,
    popular: true,
    iconSvg: BRAND_ICONS.gpay,
  },
  {
    id: "UPI_PAYTM",
    name: "Paytm UPI / Wallet",
    category: "UPI",
    currency: "INR",
    network: "UPI",
    badge: "INSTANT",
    badgeColor: "#06b6d4",
    speed: "⚡ Instant (<3s)",
    fee: "0% Free",
    minAmount: 500,
    maxAmount: 200000,
    popular: true,
    iconSvg: BRAND_ICONS.paytm,
  },
  {
    id: "UPI_BHARATPE",
    name: "BharatPe Dynamic QR",
    category: "UPI",
    currency: "INR",
    network: "UPI",
    badge: "ALL UPI APPS",
    badgeColor: "#10b981",
    speed: "⚡ Instant (<3s)",
    fee: "0% Free",
    minAmount: 500,
    maxAmount: 500000,
    iconSvg: BRAND_ICONS.bharatpe,
  },
  {
    id: "UPI_BHIM",
    name: "BHIM / Any Bank UPI",
    category: "UPI",
    currency: "INR",
    network: "UPI",
    badge: "NPCI DIRECT",
    badgeColor: "#f59e0b",
    speed: "⚡ Instant (<3s)",
    fee: "0% Free",
    minAmount: 500,
    maxAmount: 200000,
    iconSvg: BRAND_ICONS.bhim,
  },

  // Crypto Rails
  {
    id: "USDT_TRC20",
    name: "Tether USDT (TRC-20 / TRON)",
    category: "CRYPTO",
    currency: "USDT",
    network: "TRC20",
    badge: "TRON NETWORK",
    badgeColor: "#ef4444",
    speed: "⚡ 1-2 mins",
    fee: "0% Free",
    minAmount: 10,
    maxAmount: 100000,
    popular: true,
    iconSvg: BRAND_ICONS.tron,
  },
  {
    id: "SOL_SPL",
    name: "Solana (SOL / SPL)",
    category: "CRYPTO",
    currency: "SOL",
    network: "SOLANA",
    badge: "SOLANA",
    badgeColor: "#14f195",
    speed: "⚡ 5-15s",
    fee: "0% Free",
    minAmount: 10,
    maxAmount: 100000,
    popular: true,
    iconSvg: BRAND_ICONS.sol,
  },
  {
    id: "TON_MAINNET",
    name: "Toncoin TON / USDT TON",
    category: "CRYPTO",
    currency: "TON",
    network: "TON",
    badge: "TELEGRAM",
    badgeColor: "#0284c7",
    speed: "⚡ 5-10s",
    fee: "0% Free",
    minAmount: 10,
    maxAmount: 50000,
    iconSvg: BRAND_ICONS.ton,
  },
  {
    id: "BTC_NATIVE",
    name: "Bitcoin (BTC Native)",
    category: "CRYPTO",
    currency: "BTC",
    network: "BITCOIN",
    badge: "ON-CHAIN",
    badgeColor: "#f97316",
    speed: "⏳ 10-30 mins",
    fee: "0% Free",
    minAmount: 50,
    maxAmount: 250000,
    iconSvg: BRAND_ICONS.btc,
  },
  {
    id: "ETH_ERC20",
    name: "Ethereum (ETH / ERC-20)",
    category: "CRYPTO",
    currency: "ETH",
    network: "ERC20",
    badge: "ERC-20",
    badgeColor: "#6366f1",
    speed: "⚡ 2-5 mins",
    fee: "0% Free",
    minAmount: 50,
    maxAmount: 250000,
    iconSvg: BRAND_ICONS.eth,
  },

  // E-Wallets & Fiat
  {
    id: "PIX_INSTANT",
    name: "PIX Instant Pay (BRL)",
    category: "FIAT",
    currency: "BRL",
    badge: "LATAM",
    badgeColor: "#14b8a6",
    speed: "⚡ Instant (<5s)",
    fee: "0% Free",
    minAmount: 10,
    maxAmount: 50000,
    iconSvg: BRAND_ICONS.pix,
  },
  {
    id: "BANK_WIRE_IMPS",
    name: "Fast IMPS / Bank Wire",
    category: "FIAT",
    currency: "INR",
    badge: "HIGH LIMITS",
    badgeColor: "#64748b",
    speed: "⚡ 5-15 mins",
    fee: "0% Free",
    minAmount: 1000,
    maxAmount: 1000000,
    iconSvg: BRAND_ICONS.bank,
  },
];

export class CashierDepositModal {
  private options: CashierOptions;
  private currentMode: "DEPOSIT" | "WITHDRAWAL" | "HISTORY" = "DEPOSIT";
  private currentCategory: "UPI" | "CRYPTO" | "FIAT" = "UPI";
  private selectedMethod: PaymentMethodItem;
  private selectedAmount: number = 100;
  private selectedCurrency: "USD" | "INR" = "USD";
  private targetAccountCurrency: string = "INR";
  private selectedBonusId: string = "";
  private userDeclinedBonus: boolean = false;
  private activeStep: 1 | 2 = 1;

  private customMethods: any[] = [];
  private availableBonuses: any[] = [];
  private activeBonuses: any[] = [];
  private walletSummary: any = null;
  private cashierTransactions: any[] = [];
  private isLoadingHistory: boolean = false;

  private currentDepositResponse: any = null;
  private countdownTimerInterval: any = null;
  private countdownSecondsRemaining: number = 900; // 15 mins

  private cryptoRates: Record<string, number> = {
    BTC: 87500.0,
    ETH: 2800.0,
    TON: 1.60,
    USDT: 1.0,
    USDC: 1.0,
  };

  private formatCryptoAmount(amount: number, currency: string): string {
    const c = currency.toUpperCase();
    if (c === "BTC") return amount < 1 ? amount.toFixed(6) : amount.toFixed(4);
    if (c === "ETH") return amount.toFixed(4);
    if (c === "TON") return amount.toFixed(2);
    return amount.toFixed(2);
  }

  constructor(options: CashierOptions) {
    this.options = options;
    this.selectedMethod = DEFAULT_METHODS[0]; // PhonePe
  }

  private getCompatibleBonuses(): any[] {
    const isINR = this.targetAccountCurrency === "INR" || this.selectedCurrency === "INR" || this.selectedMethod?.category === "UPI" || this.currentCategory === "UPI";
    const list = this.availableBonuses && this.availableBonuses.length > 0
      ? this.availableBonuses
      : [
          {
            campaign_id: isINR ? "11111111-2222-3333-4444-555555555553" : "11111111-2222-3333-4444-555555555551",
            name: isINR ? "100% First Deposit Match (INR)" : "100% First Deposit Match",
            bonus_type: "DEPOSIT_MATCH",
            match_percentage_bps: 10000,
            turnover_multiplier: 35,
            currency: isINR ? "INR" : "USD",
            min_deposit_cents: 1000,
            max_bonus_cents: 100000,
          },
          {
            campaign_id: isINR ? "inr-boost-50" : "welcome-50",
            name: "Institutional 50% Boost",
            bonus_type: "DEPOSIT_MATCH",
            match_percentage_bps: 5000,
            turnover_multiplier: 25,
            currency: isINR ? "INR" : "USD",
            min_deposit_cents: 1000,
            max_bonus_cents: 50000,
          }
        ];

    return list.filter((b) => {
      const bCurr = String(b.currency || "USD").toUpperCase();
      if (isINR) {
        return bCurr === "INR" || bCurr === "ALL";
      } else {
        return bCurr === "USD" || bCurr === "USDT" || bCurr === "USDC" || bCurr === "ALL";
      }
    });
  }

  private syncSelectedBonus() {
    if (this.userDeclinedBonus) {
      this.selectedBonusId = "";
      return;
    }
    if (this.activeBonuses && this.activeBonuses.length > 0) {
      this.selectedBonusId = "";
      this.userDeclinedBonus = true;
      return;
    }
    const compatible = this.getCompatibleBonuses();
    if (compatible.length === 0) {
      this.selectedBonusId = "";
      return;
    }
    const alreadyValid = compatible.some((b) => String(b.campaign_id) === this.selectedBonusId);
    if (!alreadyValid && !this.userDeclinedBonus) {
      const bonus100 = compatible.find((b) => Number(b.match_percentage_bps || 0) >= 10000);
      this.selectedBonusId = String(bonus100 ? bonus100.campaign_id : compatible[0].campaign_id);
    }
  }

  public async open(mode: "DEPOSIT" | "WITHDRAWAL" | "HISTORY" = "DEPOSIT", preferredBonusId?: string) {
    this.currentMode = mode;
    this.activeStep = 1;
    if (preferredBonusId !== undefined) {
      this.selectedBonusId = preferredBonusId;
      this.userDeclinedBonus = preferredBonusId === "" || preferredBonusId === "none";
    }

    // Refresh live balance and state asynchronously so modal shows exact live balance
    void this.options.appState?.refreshLiveState?.().then(() => {
      this.render();
    });

    const modal = document.getElementById("modal-overlay");
    if (!modal) return;

    if (this.currentMode === "HISTORY") {
      try {
        this.isLoadingHistory = true;
        const txRes = await this.options.restClient.getCashierTransactions().catch(() => ({ transactions: [] }));
        this.cashierTransactions = txRes?.transactions || [];
      } catch {
        this.cashierTransactions = [];
      } finally {
        this.isLoadingHistory = false;
      }
    }

    if (this.currentMode === "WITHDRAWAL") {
      try {
        this.options.currentProfile = await this.options.restClient.getProfile();
        const activeCurr = this.options.appState?.getSelectedCurrency?.() || this.selectedCurrency || "USD";
        this.walletSummary = await this.options.restClient.getWalletSummary(activeCurr).catch(() => null);
      } catch {
        this.options.showDashboardNotice("KYC status could not be verified; withdrawal is temporarily unavailable.", "error");
        return;
      }
      const kycVerified =
        Number(this.options.currentProfile?.kyc_tier || 0) >= 1 &&
        String(this.options.currentProfile?.kyc_status || "").toUpperCase() === "VERIFIED";
      if (!kycVerified) {
        this.options.showDashboardNotice("Get your KYC verified to process withdrawals.", "error");
        this.options.showComplianceModal(this.options.currentProfile, {});
        return;
      }
    }

    if (this.currentMode === "DEPOSIT") {
      try {
        this.options.currentProfile = await this.options.restClient.getProfile();
      } catch {}

      const countryCode = String((this.options.currentProfile as any)?.country_code || (this.options.currentProfile as any)?.country || "").toUpperCase().trim();
      const isIndian = countryCode === "IN";
      const activeCurr = String(this.options.appState?.getSelectedCurrency?.() || "").toUpperCase();
      const preferred = String((this.options.currentProfile as any)?.preferred_currency || (this.options.currentProfile as any)?.currency || "").toUpperCase();
      const nativeCurr = activeCurr || preferred || (isIndian ? "INR" : "USD");

      this.targetAccountCurrency = nativeCurr;
      this.selectedCurrency = nativeCurr;
      this.currentCategory = (isIndian && nativeCurr === "INR") ? "UPI" : "CRYPTO";

      if (nativeCurr === "INR") {
        this.selectedAmount = 5000;
        this.selectedMethod = isIndian
          ? (DEFAULT_METHODS.find((m) => m.category === "UPI") || DEFAULT_METHODS[0])
          : (DEFAULT_METHODS.find((m) => m.id === "USDT_TRC20") || DEFAULT_METHODS[5]);
      } else {
        const liveFx = getLiveUsdFxRate(nativeCurr);
        this.selectedAmount = (nativeCurr === "USD" || nativeCurr === "USDT" || nativeCurr === "USDC") ? 100 : Math.round(100 * liveFx);
        this.selectedMethod = DEFAULT_METHODS.find((m) => m.id === "USDT_TRC20") || DEFAULT_METHODS.find((m) => m.category === "CRYPTO") || DEFAULT_METHODS[5];
      }

      try {
        this.customMethods = (await this.options.restClient.getTenantSettings("PAYMENT_METHOD")).items || [];
      } catch {
        this.customMethods = [];
      }
      try {
        const [bonusRes, activeRes] = await Promise.all([
          this.options.restClient.getAvailableBonuses().catch(() => ({ available_bonuses: [] })),
          this.options.restClient.getBonusProgress().catch(() => null),
        ]);
        this.availableBonuses = bonusRes?.available_bonuses || [];
        this.activeBonuses = activeRes?.active_bonuses || (activeRes?.grant_id ? [activeRes] : []);
        if (this.activeBonuses.length > 0) {
          this.userDeclinedBonus = true;
          this.selectedBonusId = "";
        } else {
          this.syncSelectedBonus();
        }
      } catch {
        this.availableBonuses = [];
        this.activeBonuses = [];
        this.selectedBonusId = "";
      }
    }

    try {
      const ratesRes = await this.options.restClient.getCryptoRates();
      if (ratesRes?.rates) {
        this.cryptoRates = { ...this.cryptoRates, ...ratesRes.rates };
      }
      if ((ratesRes as any)?.fiat_rates) {
        cacheWorldFxRates((ratesRes as any).fiat_rates);
      }
    } catch {}

    modal.style.display = "flex";
    this.render();
  }

  public close() {
    if (this.countdownTimerInterval) {
      clearInterval(this.countdownTimerInterval);
      this.countdownTimerInterval = null;
    }
    const modal = document.getElementById("modal-overlay");
    if (modal) {
      modal.style.display = "none";
      modal.innerHTML = "";
    }
  }

  private render() {
    const modal = document.getElementById("modal-overlay");
    if (!modal) return;

    const activeCurrency = this.options.appState?.getSelectedCurrency?.() || this.selectedCurrency || "USD";
    const liveBalObj = this.options.appState?.getLiveBalance?.();
    const liveBalVal = liveBalObj ? Number(liveBalObj.available_balance || 0) : 0;
    const liveBalanceFormatted = formatSmartBalance(liveBalVal);
    const currSym = getCurrencySymbol(liveBalObj?.currency || activeCurrency);
    const isDeposit = this.currentMode === "DEPOSIT";
    const isWithdrawal = this.currentMode === "WITHDRAWAL";
    const isHistory = this.currentMode === "HISTORY";

    modal.innerHTML = `
      <div class="starx-cashier-overlay" style="
        position: relative;
        background: #0d131f;
        border: 1px solid #1e293b;
        box-shadow: 0 25px 60px -12px rgba(0, 0, 0, 0.85), 0 0 0 1px rgba(56, 189, 248, 0.12);
        border-radius: 16px;
        width: min(920px, 96vw);
        max-height: 92vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, sans-serif;
        color: #f8fafc;
      ">
        <!-- TOP HEADER BAR -->
        <header style="
          padding: 12px 20px;
          background: linear-gradient(180deg, #131c2e 0%, #0d131f 100%);
          border-bottom: 1px solid #1e293b;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        ">
          <div style="display: flex; align-items: center; gap: 12px;">
            <div style="
              width: 40px;
              height: 40px;
              border-radius: 10px;
              background: linear-gradient(135deg, rgba(56, 189, 248, 0.2), rgba(16, 185, 129, 0.2));
              border: 1px solid rgba(56, 189, 248, 0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              color: #38bdf8;
            ">
              ${BRAND_ICONS.bolt}
            </div>
            <div>
              <div style="display: flex; align-items: center; gap: 8px;">
                <h3 style="margin: 0; font-size: 17px; font-weight: 700; letter-spacing: -0.01em; color: #ffffff;">
                  ${isDeposit ? "Fast & Secure Deposit" : isWithdrawal ? "Instant Fund Withdrawal" : "Cashier Transactions"}
                </h3>
                <span style="
                  font-size: 11px;
                  background: rgba(16, 185, 129, 0.15);
                  color: #34d399;
                  border: 1px solid rgba(16, 185, 129, 0.3);
                  padding: 2px 8px;
                  border-radius: 20px;
                  font-weight: 600;
                  display: flex;
                  align-items: center;
                  gap: 4px;
                ">
                  ${BRAND_ICONS.shield} 256-Bit SSL
                </span>
              </div>
              <p style="margin: 2px 0 0; font-size: 12px; color: #94a3b8;">
                0% Deposit Fees • Automated High-Speed Processing • Instant Account Crediting
              </p>
            </div>
          </div>

          <!-- Live Balance & Close -->
          <div style="display: flex; align-items: center; gap: 14px;">
            <div style="
              background: #080d16;
              border: 1px solid #1e293b;
              padding: 6px 14px;
              border-radius: 8px;
              text-align: right;
            ">
              <div style="font-size: 10px; text-transform: uppercase; color: #64748b; font-weight: 700;">Live Balance (${activeCurrency})</div>
              <div style="font-size: 14px; font-weight: 700; color: #10b981;">${currSym}${liveBalanceFormatted}</div>
            </div>
            <button id="cashier-close" style="
              background: #1e293b;
              border: 1px solid #334155;
              color: #94a3b8;
              width: 34px;
              height: 34px;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 18px;
              cursor: pointer;
              transition: all 0.15s ease;
            ">
              ✕
            </button>
          </div>
        </header>

        <!-- MODE NAVIGATION TABS -->
        <nav style="
          display: flex;
          background: #0a0f19;
          border-bottom: 1px solid #1e293b;
          padding: 0 24px;
          gap: 4px;
        ">
          <button id="tab-deposit" style="
            padding: 12px 18px;
            background: none;
            border: none;
            border-bottom: 2px solid ${isDeposit ? "#10b981" : "transparent"};
            color: ${isDeposit ? "#10b981" : "#94a3b8"};
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
          ">
            ⚡ Deposit Funds
          </button>
          <button id="tab-withdraw" style="
            padding: 12px 18px;
            background: none;
            border: none;
            border-bottom: 2px solid ${isWithdrawal ? "#38bdf8" : "transparent"};
            color: ${isWithdrawal ? "#38bdf8" : "#94a3b8"};
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
          ">
            💳 Withdraw Funds
          </button>
          <button id="tab-history" style="
            padding: 12px 18px;
            background: none;
            border: none;
            border-bottom: 2px solid ${isHistory ? "#a855f7" : "transparent"};
            color: ${isHistory ? "#a855f7" : "#94a3b8"};
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
          ">
            📜 History
          </button>
        </nav>

        <!-- MODAL BODY -->
        <main style="
          flex: 1;
          overflow-y: auto;
          padding: 16px 20px;
        ">
          ${isDeposit ? this.renderDepositBody() : isWithdrawal ? this.renderWithdrawalBody() : this.renderHistoryBody()}
        </main>
      </div>
    `;

    this.attachEventListeners();
  }

  // ==========================================
  // DEPOSIT BODY (Step 1 vs Step 2)
  // ==========================================
  private renderDepositBody(): string {
    if (this.activeStep === 2) {
      return this.renderStep2Checkout();
    }
    return this.renderStep1Selection();
  }

  private renderCryptoBannerHtml(amount: number): string {
    const targetCurr = (this.targetAccountCurrency || "USD").toUpperCase();
    const liveRate = getLiveUsdFxRate(targetCurr);
    const targetSym = getCurrencySymbol(targetCurr);
    const methodCurr = (this.selectedMethod.currency || "USDT").toUpperCase();
    const cryptoPrice = this.cryptoRates[methodCurr] || 1.0;
    const isCrypto = this.selectedMethod.category === "CRYPTO";

    if (targetCurr !== "USD" && targetCurr !== "USDT" && targetCurr !== "USDC" && isCrypto) {
      const localAmt = this.selectedCurrency === targetCurr ? amount : amount * liveRate;
      const usdAmt = localAmt / (liveRate || 1.0);
      const cryptoAmt = methodCurr === "USDT" || methodCurr === "USDC" ? usdAmt : (usdAmt / cryptoPrice);
      const cryptoFormatted = this.formatCryptoAmount(cryptoAmt, methodCurr);
      const rateText = methodCurr === "USDT" || methodCurr === "USDC"
        ? `1 ${methodCurr} = ${targetSym}${liveRate >= 100 ? liveRate.toFixed(2) : liveRate.toFixed(4)}`
        : `1 ${methodCurr} ≈ $${cryptoPrice >= 1000 ? Math.round(cryptoPrice).toLocaleString('en-US') : cryptoPrice.toFixed(2)} (${targetSym}${Math.round(cryptoPrice * liveRate).toLocaleString('en-US')})`;

      return `
        <div id="cashier-fx-banner" style="
          margin-top: 6px;
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.12) 100%);
          border: 1px solid rgba(56, 189, 248, 0.35);
          border-radius: 8px;
          padding: 7px 11px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 11px;
        ">
          <div>
            <span style="color: #94a3b8; font-size: 9.5px; text-transform: uppercase; font-weight: 700; display: block;">Guaranteed FX Rate</span>
            <strong id="fx-rate-display" style="color: #38bdf8;">${rateText}</strong>
          </div>
          <div style="text-align: right;">
            <span style="color: #94a3b8; font-size: 9.5px; text-transform: uppercase; font-weight: 700; display: block;">You Pay ──▶ Credited to ${targetCurr} Account</span>
            <strong style="color: #f8fafc;">
              <span id="fx-pay-display" style="color: #34d399;">${cryptoFormatted} ${methodCurr}</span>
              <span style="color: #64748b; margin: 0 4px;">➔</span>
              <span id="fx-credit-display" style="color: #38bdf8;">${targetSym}${Math.round(localAmt).toLocaleString()} ${targetCurr}</span>
            </strong>
          </div>
        </div>
      `;
    } else if (this.targetAccountCurrency === "USD" && isCrypto) {
      const usdAmt = this.selectedCurrency === "USD" ? amount : amount / (liveRate || 1.0);
      if (methodCurr === "USDT" || methodCurr === "USDC") {
        return `
          <div id="cashier-fx-banner" style="
            margin-top: 6px;
            background: rgba(16, 185, 129, 0.08);
            border: 1px solid rgba(16, 185, 129, 0.25);
            border-radius: 8px;
            padding: 6px 10px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 11px;
          ">
            <div style="color: #94a3b8;">Destination: <strong style="color: #ffffff;">USD Global Account</strong></div>
            <div style="color: #34d399; font-weight: 700;">1:1 Direct Deposit · 0% Fee</div>
          </div>
        `;
      } else {
        const cryptoAmt = usdAmt / cryptoPrice;
        const cryptoFormatted = this.formatCryptoAmount(cryptoAmt, methodCurr);
        const rateText = `1 ${methodCurr} ≈ $${cryptoPrice >= 1000 ? Math.round(cryptoPrice).toLocaleString('en-US') : cryptoPrice.toFixed(2)}`;
        return `
          <div id="cashier-fx-banner" style="
            margin-top: 6px;
            background: linear-gradient(135deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.12) 100%);
            border: 1px solid rgba(56, 189, 248, 0.35);
            border-radius: 8px;
            padding: 7px 11px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 11px;
          ">
            <div>
              <span style="color: #94a3b8; font-size: 9.5px; text-transform: uppercase; font-weight: 700; display: block;">Live Binance Rate</span>
              <strong id="fx-rate-display" style="color: #38bdf8;">${rateText}</strong>
            </div>
            <div style="text-align: right;">
              <span style="color: #94a3b8; font-size: 9.5px; text-transform: uppercase; font-weight: 700; display: block;">You Pay ──▶ Credited to USD Account</span>
              <strong style="color: #f8fafc;">
                <span id="fx-pay-display" style="color: #34d399;">${cryptoFormatted} ${methodCurr}</span>
                <span style="color: #64748b; margin: 0 4px;">➔</span>
                <span id="fx-credit-display" style="color: #38bdf8;">$${usdAmt.toFixed(2)} USD</span>
              </strong>
            </div>
          </div>
        `;
      }
    } else if (this.targetAccountCurrency === "USD" && this.selectedMethod.category === "UPI") {
      return `
        <div id="cashier-fx-banner" style="
          margin-top: 6px;
          background: rgba(56, 189, 248, 0.08);
          border: 1px solid rgba(56, 189, 248, 0.25);
          border-radius: 8px;
          padding: 6px 10px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 11px;
        ">
          <div>
            <span style="color: #94a3b8; font-size: 9.5px; text-transform: uppercase; font-weight: 700; display: block;">UPI ➔ USD Conversion</span>
            <strong id="fx-rate-display" style="color: #38bdf8;">1 USD = ₹${liveRate.toFixed(2)}</strong>
          </div>
          <div style="text-align: right;">
            <span style="color: #94a3b8; font-size: 9.5px; text-transform: uppercase; font-weight: 700; display: block;">Crediting to USD Account</span>
            <strong id="fx-pay-display" style="color: #34d399;">$${(this.selectedCurrency === "USD" ? amount : amount / liveRate).toFixed(2)} USD</strong>
          </div>
        </div>
      `;
    }
    return '';
  }

  // STEP 1: Method selection, Quick Amount chips, Deposit Bonus
  private renderStep1Selection(): string {
    const countryCode = String((this.options.currentProfile as any)?.country_code || (this.options.currentProfile as any)?.country || "").toUpperCase().trim();
    const isIndian = upiEnabledFor(countryCode, this.customMethods);

    const allMethods = this.getAllAvailableMethods();
    const upiMethods = isIndian ? allMethods.filter((m) => m.category === "UPI") : [];
    const cryptoMethods = allMethods.filter((m) => m.category === "CRYPTO");
    const fiatMethods: PaymentMethodItem[] = [];

    if (!isIndian && this.currentCategory === "UPI") {
      this.currentCategory = "CRYPTO";
      this.selectedCurrency = "USD";
      this.selectedAmount = 100;
      this.selectedMethod = cryptoMethods[0] || DEFAULT_METHODS[5];
    }

    const activeList =
      this.currentCategory === "UPI"
        ? upiMethods
        : this.currentCategory === "CRYPTO"
        ? cryptoMethods
        : fiatMethods;

    let presets = [25, 50, 100, 250, 500, 1000, 2500, 5000];
    if (this.selectedCurrency === "INR") {
      presets = [1000, 2500, 5000, 10000, 25000, 50000, 100000, 200000];
    } else if (getLiveUsdFxRate(this.selectedCurrency) > 100) {
      const rate = getLiveUsdFxRate(this.selectedCurrency);
      presets = [25, 50, 100, 250, 500, 1000, 2500, 5000].map(u => Math.round(u * rate));
    }

    this.syncSelectedBonus();
    const targetCurr = (this.targetAccountCurrency || "USD").toUpperCase();
    const targetFxRate = getLiveUsdFxRate(targetCurr);
    const currSymbol = getCurrencySymbol(targetCurr);
    let targetDepEquity: number;
    if (this.selectedCurrency === targetCurr) {
      targetDepEquity = this.selectedAmount;
    } else {
      const srcRate = getLiveUsdFxRate(this.selectedCurrency);
      const usdAmt = srcRate > 0 ? this.selectedAmount / srcRate : this.selectedAmount;
      targetDepEquity = usdAmt * targetFxRate;
    }
    const selectedBonus = this.getCompatibleBonuses().find((b) => String(b.campaign_id) === this.selectedBonusId);
    const matchMultiplier = selectedBonus ? (Number(selectedBonus.match_percentage_bps || 0) / 10000) : 0;
    const depositBonusAmt = (this.userDeclinedBonus || !this.selectedBonusId) ? 0 : (targetDepEquity * matchMultiplier);
    const totalTradingPower = (targetDepEquity + depositBonusAmt).toFixed(2);

    const compatibleBonuses = this.getCompatibleBonuses();
    const isNoBonusSelected = Boolean(this.userDeclinedBonus || !this.selectedBonusId);

    const bonusCardsHtml = compatibleBonuses.map((b) => {
      const matchPct = Number(b.match_percentage_bps || 0) / 100;
      const is100 = matchPct >= 100;
      const wager = b.turnover_multiplier || 35;
      const isSelected = !this.userDeclinedBonus && this.selectedBonusId === String(b.campaign_id);
      return `
        <label class="desktop-bonus-option-card bonus-card ${isSelected ? 'active' : ''}" data-bonus-id="${this.options.escapeHTML(String(b.campaign_id))}" style="
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 7px 10px;
          background: ${isSelected ? 'linear-gradient(135deg, rgba(14, 165, 233, 0.16) 0%, rgba(56, 189, 248, 0.08) 100%)' : '#0a0f18'};
          border: 1.5px solid ${isSelected ? '#38bdf8' : '#1e293b'};
          border-radius: 6px;
          cursor: pointer;
          margin-bottom: 5px;
          transition: all 0.15s ease;
        ">
          <div style="display: flex; align-items: center; gap: 8px;">
            <input type="radio" name="desktop-bonus-campaign" value="${this.options.escapeHTML(String(b.campaign_id))}" ${isSelected ? 'checked' : ''} style="accent-color: #38bdf8; width: 14px; height: 14px; cursor: pointer;" />
            <div>
              <div class="bonus-title-val" style="font-size: 11.5px; font-weight: 700; color: ${isSelected ? '#ffffff' : '#e2e8f0'}; display: flex; align-items: center; gap: 5px;">
                ${is100 ? '🎁 100% WELCOME MATCH' : this.options.escapeHTML(String(b.name))}
              </div>
              <div style="font-size: 10px; color: #94a3b8; margin-top: 1px;">+${matchPct.toFixed(0)}% Match · ${wager}× Turnover</div>
            </div>
          </div>
          <span style="font-size: 10.5px; font-weight: 800; color: #34d399; background: rgba(52, 211, 153, 0.12); border: 1px solid rgba(52, 211, 153, 0.25); padding: 2px 7px; border-radius: 999px;">
            +${matchPct.toFixed(0)}%
          </span>
        </label>
      `;
    }).join("");

    const noBonusCardHtml = `
      <label class="desktop-bonus-option-card no-bonus-card ${isNoBonusSelected ? 'active' : ''}" data-bonus-id="" style="
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 7px 10px;
        background: ${isNoBonusSelected ? 'rgba(56, 189, 248, 0.08)' : '#0a0f18'};
        border: 1.5px ${isNoBonusSelected ? 'solid #38bdf8' : 'dashed #1e293b'};
        box-shadow: ${isNoBonusSelected ? '0 0 12px rgba(56, 189, 248, 0.15)' : 'none'};
        border-radius: 6px;
        cursor: pointer;
        margin-bottom: 4px;
        transition: all 0.15s ease;
      ">
        <div style="display: flex; align-items: center; gap: 8px;">
          <input type="radio" name="desktop-bonus-campaign" value="" ${isNoBonusSelected ? 'checked' : ''} style="accent-color: #38bdf8; width: 14px; height: 14px; cursor: pointer;" />
          <span class="bonus-title-val" style="font-size: 11px; font-weight: ${isNoBonusSelected ? '700' : '600'}; color: ${isNoBonusSelected ? '#ffffff' : '#94a3b8'};">Standard Deposit (No bonus offer)</span>
        </div>
        <span style="font-size: 9.5px; color: ${isNoBonusSelected ? '#38bdf8' : '#64748b'}; font-weight: 700;">0× Wager</span>
      </label>
    `;

    const bonusOptions = compatibleBonuses
      .map(
        (b) => {
          const matchPct = Number(b.match_percentage_bps || 0) / 100;
          const is100 = matchPct >= 100;
          const wager = b.turnover_multiplier || 35;
          const isSelected = !this.userDeclinedBonus && this.selectedBonusId === String(b.campaign_id);
          return `<option value="${this.options.escapeHTML(String(b.campaign_id))}" ${
            isSelected ? "selected" : ""
          }>${is100 ? '🎁 100% REGISTRATION BONUS · ' : ''}${this.options.escapeHTML(String(b.name))} · +${matchPct.toFixed(0)}% Bonus (${wager}x Wager)</option>`;
        }
      )
      .join("");

    return `
      <!-- COMPATIBILITY HIDDEN FIELDS (For Automated Tests & RestClient) -->
      <div style="display: none;">
        <select id="cashier-currency">
          <option value="USD" ${this.selectedCurrency === "USD" ? "selected" : ""}>USD</option>
          <option value="USDT" ${this.selectedCurrency === "USD" ? "selected" : ""}>USDT</option>
          <option value="INR" ${this.selectedCurrency === "INR" ? "selected" : ""}>INR</option>
          <option value="USDC">USDC</option>
        </select>
        <select id="cashier-network">
          ${allMethods
            .map(
              (m) =>
                `<option value="${m.id}" ${
                  this.selectedMethod.id === m.id ? "selected" : ""
                }>${m.name}</option>`
            )
            .join("")}
        </select>
      </div>

      <!-- UNIVERSAL TARGET WALLET SELECTOR (StarX Global Multi-Currency Standard) -->
      <div class="target-wallet-picker-bar" style="
        background: #080d16;
        border: 1px solid #1e293b;
        border-radius: 10px;
        padding: 8px 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;
        gap: 12px;
      ">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="font-size: 11px; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em;">
            💼 Deposit To Account:
          </span>
          <div style="display: flex; gap: 6px;">
            ${(this.targetAccountCurrency !== "USD" && this.targetAccountCurrency !== "USDT") ? `
            <button type="button" class="btn-target-wallet" data-target="${this.targetAccountCurrency}" style="
              padding: 5px 12px;
              border-radius: 6px;
              border: 1.5px solid ${this.targetAccountCurrency !== "USD" ? "#38bdf8" : "#334155"};
              background: ${this.targetAccountCurrency !== "USD" ? "#0284c7" : "#0f1624"};
              color: #ffffff;
              font-weight: 700;
              font-size: 11.5px;
              cursor: pointer;
              display: flex;
              align-items: center;
              gap: 5px;
              transition: all 0.15s ease;
            ">
              🌐 ${this.targetAccountCurrency} Account (${getCurrencySymbol(this.targetAccountCurrency)})
            </button>
            ` : ''}
            <button type="button" class="btn-target-wallet" data-target="USD" style="
              padding: 5px 12px;
              border-radius: 6px;
              border: 1.5px solid ${this.targetAccountCurrency === "USD" ? "#38bdf8" : "#334155"};
              background: ${this.targetAccountCurrency === "USD" ? "#0284c7" : "#0f1624"};
              color: #ffffff;
              font-weight: 700;
              font-size: 11.5px;
              cursor: pointer;
              display: flex;
              align-items: center;
              gap: 5px;
              transition: all 0.15s ease;
            ">
              🇺🇸 USD Account ($)
            </button>
          </div>
        </div>
        <div style="font-size: 11px; color: #34d399; font-weight: 700; display: flex; align-items: center; gap: 4px;">
          <span>⚡ Direct Account Crediting (${this.targetAccountCurrency})</span>
        </div>
      </div>

      <!-- ZERO-SCROLL 2-COLUMN DEPOSIT LAYOUT -->
      <div class="deposit-grid-layout" style="display: grid; grid-template-columns: 1.05fr 1fr; gap: 16px; align-items: start;">

        <!-- LEFT COLUMN: PAYMENT METHOD & SETTLEMENT RAIL -->
        <div style="display: flex; flex-direction: column; gap: 8px;">
          <!-- CATEGORY SWITCHER TABS -->
          <div style="display: flex; gap: 6px; background: #080d16; padding: 3px; border-radius: 8px; border: 1px solid #1e293b;">
            ${isIndian ? `
            <button id="cat-upi" style="
              flex: 1;
              padding: 6px 10px;
              border-radius: 6px;
              border: none;
              background: ${this.currentCategory === "UPI" ? "#1e293b" : "transparent"};
              color: ${this.currentCategory === "UPI" ? "#38bdf8" : "#94a3b8"};
              font-weight: 700;
              font-size: 11px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 5px;
              transition: all 0.15s ease;
            ">
              🇮🇳 UPI <span style="font-size: 9px; background: rgba(56, 189, 248, 0.2); color: #38bdf8; padding: 1px 4px; border-radius: 4px;">FAST</span>
            </button>
            ` : ''}
            <button id="cat-crypto" style="
              flex: 1;
              padding: 6px 10px;
              border-radius: 6px;
              border: none;
              background: ${this.currentCategory === "CRYPTO" ? "#1e293b" : "transparent"};
              color: ${this.currentCategory === "CRYPTO" ? "#34d399" : "#94a3b8"};
              font-weight: 700;
              font-size: 11px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 5px;
              transition: all 0.15s ease;
            ">
              ⚡ Instant Crypto <span style="font-size: 9px; background: rgba(52, 211, 153, 0.2); color: #34d399; padding: 1px 4px; border-radius: 4px;">RECOMMENDED</span>
            </button>
          </div>

          <!-- COMPACT METHOD TILES (2 Columns) -->
          <div style="
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 7px;
          ">
            ${activeList
              .map((m) => {
                const isSelected = this.selectedMethod.id === m.id;
                return `
                  <div class="method-card" data-method-id="${m.id}" style="
                    background: ${isSelected ? "linear-gradient(135deg, rgba(56, 189, 248, 0.16) 0%, rgba(16, 185, 129, 0.08) 100%)" : "rgba(15, 23, 42, 0.75)"};
                    border: 1.5px solid ${isSelected ? "#38bdf8" : "rgba(51, 65, 85, 0.6)"};
                    box-shadow: ${isSelected ? "0 4px 16px rgba(56, 189, 248, 0.22), inset 0 0 10px rgba(56, 189, 248, 0.1)" : "0 2px 4px rgba(0, 0, 0, 0.2)"};
                    border-radius: 10px;
                    padding: 9px 10px;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                  ">
                    <div style="display: flex; justify-content: space-between; align-items: center; gap: 4px;">
                      <div style="display: flex; align-items: center; gap: 7px; min-width: 0;">
                        ${m.iconSvg}
                        <div style="font-size: 11.5px; font-weight: 750; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${m.name}</div>
                      </div>
                      ${
                        isSelected
                          ? `<span style="font-size: 8px; font-weight: 850; background: #38bdf8; color: #020617; padding: 1px 5px; border-radius: 4px; display: flex; align-items: center; gap: 2px;">✓ ACTIVE</span>`
                          : m.badge
                          ? `<span style="
                              font-size: 8px;
                              font-weight: 800;
                              text-transform: uppercase;
                              background: ${m.badgeColor || "#38bdf8"}22;
                              color: ${m.badgeColor || "#38bdf8"};
                              border: 1px solid ${m.badgeColor || "#38bdf8"}55;
                              padding: 1px 4px;
                              border-radius: 4px;
                              white-space: nowrap;
                              flex-shrink: 0;
                            ">${m.badge}</span>`
                          : ""
                      }
                    </div>
                    <div style="display: flex; justify-content: space-between; align-items: center; font-size: 10px; color: #94a3b8; border-top: 1px solid rgba(255,255,255,0.06); padding-top: 4px;">
                      <span style="color: #34d399; font-weight: 700; display: flex; align-items: center; gap: 3px;">⚡ ${m.speed}</span>
                      <span>Min: <strong style="color: #f1f5f9;">${getCurrencySymbol(m.currency)}${m.minAmount.toLocaleString()}</strong></span>
                    </div>
                  </div>
                `;
              })
              .join("")}
          </div>

          <!-- DIRECT BLOCKCHAIN SETTLEMENT INFO -->
          ${this.selectedMethod.category === "CRYPTO" ? `
            <div style="
              background: #080d16;
              border: 1px solid #1e293b;
              border-radius: 8px;
              padding: 8px 12px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
            ">
              <div style="display: flex; align-items: center; gap: 7px;">
                <span style="font-size: 15px;">⚡</span>
                <div>
                  <div style="font-size: 11px; font-weight: 700; color: #f8fafc;">Direct Blockchain Settlement</div>
                  <div style="font-size: 9.5px; color: #64748b;">Dynamic Single-Use Dedicated Receiving Address</div>
                </div>
              </div>
              <div style="display: flex; align-items: center; gap: 5px; font-size: 10.5px; font-weight: 700; color: #10b981; background: rgba(16, 185, 129, 0.1); padding: 3px 8px; border-radius: 6px; border: 1px solid rgba(16, 185, 129, 0.2);">
                <span>🔒 Direct On-Chain Settlement</span>
              </div>
            </div>
          ` : ''}

          <div id="custom-payment-instructions"></div>
        </div>

        <!-- RIGHT COLUMN: AMOUNT, BONUS & IMMEDIATE ACTION BUTTON -->
        <div style="display: flex; flex-direction: column; gap: 8px;">

          <!-- AMOUNT INPUT & CURRENCY SWITCHER ROW -->
          <div>
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 5px;">
              <label style="font-size: 11px; font-weight: 700; color: #cbd5e1; text-transform: uppercase; letter-spacing: 0.03em;">
                Deposit Amount
              </label>
              <div style="display: flex; gap: 4px; background: #080d16; padding: 2px; border-radius: 6px; border: 1px solid #1e293b;">
                <button id="curr-usd" style="
                  padding: 2px 7px;
                  border-radius: 4px;
                  border: none;
                  background: ${this.selectedCurrency === "USD" ? "#0284c7" : "transparent"};
                  color: ${this.selectedCurrency === "USD" ? "#ffffff" : "#94a3b8"};
                  font-size: 10px;
                  font-weight: 700;
                  cursor: pointer;
                ">USD ($)</button>
                <button id="curr-inr" style="
                  padding: 2px 7px;
                  border-radius: 4px;
                  border: none;
                  background: ${this.selectedCurrency === "INR" ? "#0284c7" : "transparent"};
                  color: ${this.selectedCurrency === "INR" ? "#ffffff" : "#94a3b8"};
                  font-size: 10px;
                  font-weight: 700;
                  cursor: pointer;
                ">INR (₹)</button>
              </div>
            </div>

            <!-- CUSTOM AMOUNT INPUT -->
            <div style="position: relative;">
              <span style="
                position: absolute;
                left: 12px;
                top: 50%;
                transform: translateY(-50%);
                font-size: 14px;
                font-weight: 700;
                color: #38bdf8;
              ">${this.selectedCurrency === "INR" ? "₹" : "$"}</span>
              <input
                id="cashier-amount"
                inputmode="decimal"
                value="${this.selectedAmount}"
                placeholder="Enter deposit amount"
                style="
                  width: 100%;
                  box-sizing: border-box;
                  background: #080d16;
                  border: 1.5px solid #334155;
                  border-radius: 8px;
                  padding: 8px 12px 8px 28px;
                  font-size: 14px;
                  font-weight: 700;
                  color: #ffffff;
                  outline: none;
                  transition: all 0.15s ease;
                "
              />
            </div>

            <!-- DUAL CURRENCY FX CONVERSION BANNER -->
            ${this.renderCryptoBannerHtml(this.selectedAmount)}
          </div>

          <!-- QUICK AMOUNT CHIPS (4 columns x 2 rows) -->
          <div style="
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 5px;
          ">
            ${presets
              .map((amt) => {
                const isSelected = this.selectedAmount === amt;
                const isHot = amt === 100 || amt === 5000;
                const isVip = amt === 500 || amt === 25000;
                return `
                  <button class="amount-chip" data-amount="${amt}" style="
                    background: ${isSelected ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" : "rgba(15, 23, 42, 0.85)"};
                    border: 1.5px solid ${isSelected ? "#34d399" : "rgba(51, 65, 85, 0.6)"};
                    color: ${isSelected ? "#ffffff" : "#f1f5f9"};
                    box-shadow: ${isSelected ? "0 4px 14px rgba(16, 185, 129, 0.4), inset 0 0 6px rgba(255,255,255,0.2)" : "none"};
                    font-weight: 750;
                    font-size: 11.5px;
                    padding: 6px 3px;
                    border-radius: 8px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 3px;
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                  ">
                    <span>${this.selectedCurrency === "INR" ? "₹" : "$"}${amt.toLocaleString()}</span>
                    ${
                      isHot
                        ? `<span style="font-size: 7.5px; font-weight: 850; background: ${isSelected ? '#022c22' : 'rgba(239,68,68,0.2)'}; color: ${isSelected ? '#6ee7b7' : '#f87171'}; border: 1px solid ${isSelected ? '#047857' : 'rgba(239,68,68,0.4)'}; padding: 1px 3px; border-radius: 3px;">HOT★</span>`
                        : isVip
                        ? `<span style="font-size: 7.5px; font-weight: 850; background: ${isSelected ? '#451a03' : 'rgba(245,158,11,0.2)'}; color: ${isSelected ? '#fde68a' : '#fbbf24'}; border: 1px solid ${isSelected ? '#b45309' : 'rgba(245,158,11,0.4)'}; padding: 1px 3px; border-radius: 3px;">VIP👑</span>`
                        : ""
                    }
                  </button>
                `;
              })
              .join("")}
          </div>

          <!-- BONUS PROMOTION SECTION -->
          <div style="
            background: #080d16;
            border: 1px solid #1e293b;
            border-radius: 8px;
            padding: 8px 10px;
            display: flex;
            flex-direction: column;
            gap: 5px;
          ">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px;">
              <span style="font-size: 11px; font-weight: 800; color: #38bdf8; text-transform: uppercase; letter-spacing: 0.05em; display: flex; align-items: center; gap: 5px;">
                <span>🎁</span> Deposit Boost & Bonus
              </span>
              <span style="font-size: 9.5px; color: #34d399; font-weight: 700;">Guaranteed Offer</span>
            </div>

            <!-- Visual Selectable Bonus Cards (Synced with Mobile PWA) -->
            <style>
              .desktop-bonus-option-card {
                transition: all 0.15s ease !important;
              }
              .desktop-bonus-option-card.active,
              .desktop-bonus-option-card:has(input[name="desktop-bonus-campaign"]:checked) {
                border-color: #38bdf8 !important;
                background: linear-gradient(135deg, rgba(14, 165, 233, 0.16) 0%, rgba(56, 189, 248, 0.08) 100%) !important;
                box-shadow: 0 0 12px rgba(56, 189, 248, 0.15) !important;
              }
              .desktop-bonus-option-card.no-bonus-card.active,
              .desktop-bonus-option-card.no-bonus-card:has(input[name="desktop-bonus-campaign"]:checked) {
                border-color: #38bdf8 !important;
                border-style: solid !important;
                background: rgba(56, 189, 248, 0.08) !important;
                box-shadow: 0 0 12px rgba(56, 189, 248, 0.15) !important;
              }
            </style>
            <div class="desktop-bonus-cards-container" style="display: flex; flex-direction: column; gap: 2px;">
              ${bonusCardsHtml}
              ${noBonusCardHtml}
            </div>

            <select
              id="cashier-bonus"
              style="display: none;"
            >
              ${
                bonusOptions
                  ? bonusOptions
                  : `<option value="">Standard Deposit (No Promo Offer Active)</option>`
              }
              <option value="" ${(!this.selectedBonusId || this.userDeclinedBonus) ? "selected" : ""}>Continue without a bonus</option>
            </select>

            <!-- LIVE EQUITY CALCULATION PILL -->
            <div style="
              background: rgba(126, 34, 206, 0.12);
              border: 1px dashed rgba(147, 51, 234, 0.4);
              padding: 5px 8px;
              border-radius: 5px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              font-size: 10.5px;
            ">
              <div>
                <span style="color: #c084fc;">Deposit:</span> <strong id="cashier-deposit-equity-val" style="color: #ffffff;">${currSymbol}${targetDepEquity.toFixed(2)}</strong>
                <span style="color: #64748b; margin: 0 3px;">+</span>
                <span style="color: #34d399;">Bonus:</span> <strong id="cashier-bonus-equity-val" style="color: #34d399;">+${currSymbol}${depositBonusAmt.toFixed(2)}</strong>
              </div>
              <div style="font-weight: 800; color: #f8fafc; font-size: 11px;">
                🚀 Equity: <span id="cashier-total-equity-val" style="color: #38bdf8;">${currSymbol}${totalTradingPower}</span>
              </div>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; font-size: 9.5px;">
              <span id="cashier-bonus-status" aria-live="polite" style="color: #94a3b8;"></span>
              <button id="claim-existing-deposit-bonus" type="button" style="background: none; border: none; color: #a855f7; text-decoration: underline; cursor: pointer; font-size: 9.5px;">Claim prior deposit</button>
            </div>
          </div>

          <!-- STATUS NOTIFICATION RESULT -->
          <div id="cashier-result" aria-live="polite" style="font-size: 10.5px; min-height: 12px; color: #94a3b8;"></div>

          <!-- PRIMARY ACTION FOOTER (RIGHT IN FRONT OF USER - NO SCROLL) -->
          <div style="display: flex; gap: 8px; align-items: center;">
            <button id="cashier-cancel-btn" style="
              flex: 0 0 75px;
              padding: 9px 12px;
              background: #1e293b;
              color: #cbd5e1;
              border: 1px solid #334155;
              border-radius: 7px;
              font-weight: 600;
              font-size: 11.5px;
              cursor: pointer;
            ">
              Cancel
            </button>
            <button id="cashier-submit" style="
              flex: 1;
              padding: 11px 18px;
              background: linear-gradient(135deg, #10b981 0%, #059669 100%);
              color: #ffffff;
              border: none;
              border-radius: 8px;
              font-weight: 850;
              font-size: 13.5px;
              cursor: pointer;
              box-shadow: 0 4px 18px rgba(16, 185, 129, 0.45);
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              letter-spacing: 0.02em;
              transition: all 0.2s ease;
            ">
              <span>⚡ Proceed to Instant Payment</span> ➔
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // ==========================================
  // STEP 2: CHECKOUT SCREEN (QR Code, VPA, Timer, Live Webhook Radar, UTR verification)
  // ==========================================
  private renderStep2Checkout(): string {
    const isUPI = this.selectedMethod.category === "UPI";

    const intentId = String(this.currentDepositResponse?.deposit_id || "");
    const destinationAddress = String(this.currentDepositResponse?.deposit_address || "");
    const payableAmount = String(this.currentDepositResponse?.expected_amount || "");
    const payableCurrency = String(this.currentDepositResponse?.currency || "");
    if (!intentId || !destinationAddress || !payableAmount) return '<p role="alert">Verified deposit details unavailable. Reopen the deposit form.</p>';
    const formattedINR = this.options.escapeHTML(payableAmount);

    const targetCurrency = String(this.currentDepositResponse?.target_currency || this.targetAccountCurrency || payableCurrency);
    const targetAmount = String(this.currentDepositResponse?.target_amount || payableAmount);
    const fxRate = this.currentDepositResponse?.fx_rate ? String(this.currentDepositResponse.fx_rate) : null;
    const isFxConverted = targetCurrency !== payableCurrency && Boolean(fxRate);

    const paymentUrl = String(this.currentDepositResponse?.payment_url || "");
    const qrCodeData = String(this.currentDepositResponse?.qr_code_data || "");

    // Build standard NPCI UPI URI or use pure destinationAddress for crypto scanners (Bybit, Binance, Trust Wallet)
    const upiUri = isUPI ? (qrCodeData && qrCodeData.startsWith("upi://") ? qrCodeData : upiPaymentURI(intentId,destinationAddress,payableAmount,payableCurrency)) : '';
    const qrData = isUPI ? (upiUri || qrCodeData) : destinationAddress;
    const qrSvg = generatePaymentQRCodeSVG(qrData, 190);

    const minutes = Math.floor(this.countdownSecondsRemaining / 60);
    const seconds = this.countdownSecondsRemaining % 60;
    const formattedTimer = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

    const explorer = this.getBlockchainExplorer(this.selectedMethod.network || this.selectedMethod.id, destinationAddress);

    const netId = String(this.selectedMethod.network || this.selectedMethod.id || '').toUpperCase();
    const isTron = netId.includes('TRC20') || netId.includes('TRON');
    const isBsc = netId.includes('BEP20') || netId.includes('BSC');
    const isEth = netId.includes('ERC20') || netId.includes('ETH');
    const isBtc = netId.includes('BTC') || netId.includes('BITCOIN');
    const isTon = netId.includes('TON');
    const isSol = netId.includes('SOL');

    const chainName = isTron
      ? 'TRON (TRC-20)'
      : (isBsc
          ? 'BNB Smart Chain (BEP-20 / BSC)'
          : (isEth
              ? 'Ethereum Mainnet (ERC-20)'
              : (isBtc
                  ? 'Bitcoin Native (SegWit)'
                  : (isTon
                      ? 'The Open Network (TON)'
                      : (isSol
                          ? 'Solana (SPL)'
                          : this.selectedMethod.name)))));
    const chainBadgeColor = isTron
      ? '#ef4444'
      : (isBsc
          ? '#eab308'
          : (isEth
              ? '#6366f1'
              : (isBtc
                  ? '#f59e0b'
                  : (isTon
                      ? '#0284c7'
                      : '#38bdf8'))));
    const chainWarning = isTron
      ? '⚠️ Send <strong>ONLY Tether USDT via the TRON (TRC-20)</strong> network. Do not send on BSC, ERC-20, or Polygon.'
      : (isBsc
          ? '⚠️ Send <strong>ONLY Tether USDT via BNB Smart Chain (BEP-20 / BSC)</strong> network.'
          : (isEth
              ? '⚠️ Send <strong>ONLY via Ethereum Mainnet (ERC-20)</strong> network.'
              : (isBtc
                  ? '⚠️ Send <strong>ONLY Bitcoin (BTC)</strong> to this Native SegWit address.'
                  : (isTon
                      ? '⚠️ Send <strong>TON or USDT-TON</strong> via The Open Network.'
                      : 'Send exact token via the matching blockchain rail.'))));

    return `
      <div style="display: flex; flex-direction: column; gap: 18px;">
        <!-- STEP 2 NAVIGATION & TIMER BAR -->
        <div style="
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #080d16;
          border: 1px solid #1e293b;
          border-radius: 10px;
          padding: 10px 16px;
        ">
          <button id="btn-back-step1" style="
            background: none;
            border: none;
            color: #38bdf8;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
          ">
            ← Change Amount or Method
          </button>
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 11px; color: #94a3b8;">Order Valid For:</span>
            <span id="cashier-countdown" style="
              font-family: monospace;
              font-size: 14px;
              font-weight: 800;
              color: #f59e0b;
              background: rgba(245, 158, 11, 0.15);
              padding: 2px 8px;
              border-radius: 6px;
              border: 1px solid rgba(245, 158, 11, 0.3);
            ">
              ⏳ ${formattedTimer}
            </span>
          </div>
        </div>

        <!-- MAIN CHECKOUT HERO GRID -->
        <div style="
          display: grid;
          grid-template-columns: minmax(220px, 260px) 1fr;
          gap: 20px;
          background: #0f1624;
          border: 1px solid #1e293b;
          border-radius: 14px;
          padding: 20px;
        ">
          <!-- LEFT COLUMN: CRISP QR CODE -->
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: #080d16;
            border: 1px solid #1e293b;
            border-radius: 12px;
            padding: 16px;
            text-align: center;
          ">
            ${qrSvg}
            <div style="margin-top: 10px; font-size: 11px; font-weight: 700; color: #38bdf8;">
              ${isUPI ? "Scan with Any UPI App" : "Scan via Web3 / Crypto Wallet"}
            </div>
            <div style="font-size: 10px; color: #64748b; margin-top: 2px;">
              ${isUPI ? "PhonePe • GPay • Paytm • BHIM • Cred" : `${this.selectedMethod.name}`}
            </div>
            ${
              isUPI
                ? `<a href="${paymentUrl || upiUri}" target="_blank" rel="noopener noreferrer" style="
                    margin-top: 10px;
                    display: inline-block;
                    width: 100%;
                    box-sizing: border-box;
                    padding: 8px 12px;
                    background: #5f259f;
                    color: #ffffff;
                    text-decoration: none;
                    font-size: 12px;
                    font-weight: 700;
                    border-radius: 6px;
                  ">
                    📱 ${paymentUrl ? "Pay with Gateway / UPI (₹" + formattedINR + ")" : "Open UPI App (Pay ₹" + formattedINR + ")"}
                  </a>`
                : (destinationAddress && destinationAddress !== "..."
                    ? `<a href="${explorer.url}" target="_blank" rel="noopener noreferrer" id="btn-view-explorer" style="
                        margin-top: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 7px;
                        width: 100%;
                        box-sizing: border-box;
                        padding: 9px 12px;
                        background: #1e293b;
                        border: 1px solid #334155;
                        color: #38bdf8;
                        text-decoration: none;
                        font-size: 11.5px;
                        font-weight: 700;
                        border-radius: 8px;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
                        transition: all 0.15s ease;
                      ">
                        🔍 Track on ${explorer.name} ↗
                      </a>`
                    : "")
            }
          </div>

          <!-- RIGHT COLUMN: PAYMENT DETAILS & UTR INPUT -->
          <div style="display: flex; flex-direction: column; gap: 14px;">
            <!-- INTENT & AMOUNT HEADER -->
            <div style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 1px solid #1e293b;
              padding-bottom: 12px;
            ">
              <div>
                <span style="font-size: 10px; text-transform: uppercase; color: #64748b; font-weight: 700;">Intent ID:</span>
                <code id="res-deposit-id" style="font-size: 12px; color: #38bdf8; font-weight: 700; margin-left: 4px;">${intentId}</code>
              </div>
              <div style="text-align: right;">
                <div style="font-size: 11px; text-transform: uppercase; color: #94a3b8; font-weight: 700; display: flex; align-items: center; justify-content: flex-end; gap: 6px;">
                  <span>You Pay:</span>
                  <span style="font-size: 14px; font-weight: 800; color: #f8fafc;">${isUPI ? `₹${formattedINR}` : `${this.options.escapeHTML(payableAmount)} ${this.options.escapeHTML(payableCurrency)}`}</span>
                  ${!isUPI ? `
                    <button id="btn-copy-amount" data-amt="${this.options.escapeHTML(payableAmount)}" title="Copy payable amount" style="
                      background: #1e293b;
                      border: 1px solid #334155;
                      color: #38bdf8;
                      border-radius: 4px;
                      padding: 2px 6px;
                      font-size: 10px;
                      font-weight: 700;
                      cursor: pointer;
                      display: inline-flex;
                      align-items: center;
                      gap: 3px;
                    ">
                      ${BRAND_ICONS.copy} Copy
                    </button>
                  ` : ''}
                </div>
                <div style="font-size: 17px; font-weight: 800; color: #10b981; margin-top: 2px;">
                  Credited: ${getCurrencySymbol(targetCurrency)}${this.options.escapeHTML(targetAmount)} ${this.options.escapeHTML(targetCurrency)}
                </div>
                <div style="font-size: 10px; color: #64748b; margin-top: 2px;">
                  ${isFxConverted ? `⚡ Locked Rate: 1 ${payableCurrency} = ${getCurrencySymbol(targetCurrency)}${fxRate} · No extra fees` : `Direct 1:1 Credit to ${targetCurrency} Account`}
                </div>
              </div>
            </div>

            ${!isUPI ? `
              <!-- PROMINENT NETWORK WARNING & CHAIN BADGE -->
              <div style="
                display: flex;
                align-items: flex-start;
                gap: 12px;
                background: rgba(15, 23, 42, 0.95);
                border: 1px solid ${chainBadgeColor}55;
                border-radius: 10px;
                padding: 10px 14px;
                box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
              ">
                <div style="flex-shrink: 0; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; margin-top: 2px;">
                  ${this.selectedMethod.iconSvg || ''}
                </div>
                <div style="flex: 1; text-align: left;">
                  <div style="font-size: 11px; font-weight: 800; color: ${chainBadgeColor}; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 6px;">
                    <span>NETWORK CHAIN:</span>
                    <span>${this.options.escapeHTML(chainName)}</span>
                  </div>
                  <div style="font-size: 11px; color: #cbd5e1; line-height: 1.4; margin-top: 2px;">
                    ${chainWarning}
                  </div>
                </div>
              </div>
            ` : ''}

            <!-- VPA / ADDRESS COPY BOX -->
            <div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px;">
                <label style="font-size: 11px; font-weight: 700; color: #94a3b8;">
                  ${isUPI ? "Official Merchant UPI VPA Address" : `Authoritative ${this.options.escapeHTML(chainName)} Deposit Address`}
                </label>
                ${!isUPI && destinationAddress && destinationAddress !== "..." ? `
                  <a href="${explorer.url}" target="_blank" rel="noopener noreferrer" style="font-size: 10.5px; color: #38bdf8; text-decoration: none; font-weight: 600; display: flex; align-items: center; gap: 3px;">
                    View on ${explorer.name} ↗
                  </a>
                ` : ''}
              </div>
              <div style="
                display: flex;
                background: #080d16;
                border: 1px solid #334155;
                border-radius: 8px;
                overflow: hidden;
              ">
                <input
                  id="checkout-addr-display"
                  type="text"
                  readonly
                  value="${destinationAddress}"
                  style="
                    flex: 1;
                    padding: 10px 12px;
                    background: transparent;
                    border: none;
                    color: #ffffff;
                    font-family: monospace;
                    font-size: 13px;
                    font-weight: 600;
                    outline: none;
                  "
                />
                <button id="btn-copy-address" style="
                  padding: 0 16px;
                  background: #1e293b;
                  border: none;
                  border-left: 1px solid #334155;
                  color: #38bdf8;
                  font-size: 12px;
                  font-weight: 700;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  gap: 6px;
                ">
                  ${BRAND_ICONS.copy} Copy
                </button>
              </div>
            </div>

            <!-- LIVE INGESTION RADAR ANIMATION -->
            <div style="
              background: rgba(16, 185, 129, 0.08);
              border: 1px solid rgba(16, 185, 129, 0.25);
              border-radius: 8px;
              padding: 10px 14px;
              display: flex;
              align-items: center;
              gap: 10px;
            ">
              <div style="
                width: 10px;
                height: 10px;
                border-radius: 50%;
                background: #10b981;
                box-shadow: 0 0 10px #10b981;
                animation: pulse 1.5s infinite;
              "></div>
              <div style="font-size: 11px; color: #a7f3d0; line-height: 1.4;">
                ${isUPI ? '<strong>Payment verification pending:</strong> Credit requires bank settlement verification or authorized finance approval.' : '<strong>Waiting for Blockchain Confirmation:</strong> Funds will credit automatically to your trading balance once confirmed on-chain.'}
              </div>
            </div>

            <!-- UTR / TRANSACTION HASH PROOF SUBMISSION FORM -->
            <div class="deposit-proof-box" style="
              background: #080d16;
              border: 1px solid #1e293b;
              border-radius: 10px;
              padding: 14px;
              display: flex;
              flex-direction: column;
              gap: 8px;
            ">
              <label for="deposit-transaction-hash" style="font-size: 12px; font-weight: 700; color: #f8fafc; display: flex; justify-content: space-between;">
                <span>${isUPI ? "Submit 12-Digit UPI UTR Reference Number" : "Submit Blockchain Transaction Hash (TxID)"}</span>
                <span style="color: #38bdf8; font-size: 11px;">Payment Reference</span>
              </label>
              <p style="margin: 0; font-size: 11px; color: #94a3b8;">
                ${isUPI ? "After transferring funds in PhonePe/GPay/Paytm, copy the 12-digit UTR/Ref No. from your receipt:" : "Paste the transaction hash / TxID for verification. You can track confirmation status directly on public blockchain explorers."}
              </p>
              <div style="display: flex; gap: 8px;">
                <input
                  id="deposit-transaction-hash"
                  autocomplete="off"
                  placeholder="${isUPI ? 'e.g. 428910293847 (12 digits)' : 'e.g. 0x8a7f... or TRX hash'}"
                  style="
                    flex: 1;
                    padding: 10px 12px;
                    background: #0d131f;
                    border: 1px solid #334155;
                    border-radius: 8px;
                    color: #ffffff;
                    font-family: monospace;
                    font-size: 13px;
                    outline: none;
                  "
                />
                <button id="submit-deposit-proof" style="
                  padding: 10px 18px;
                  background: #0284c7;
                  color: #ffffff;
                  border: none;
                  border-radius: 8px;
                  font-weight: 700;
                  font-size: 13px;
                  cursor: pointer;
                  white-space: nowrap;
                ">
                  Submit Proof
                </button>
              </div>
              <div id="deposit-proof-status" role="status" style="font-size: 11px; min-height: 16px; margin-top: 2px;"></div>
            </div>
          </div>
        </div>

        <!-- COMPATIBILITY RESULT CONTAINER -->
        <div id="cashier-result" aria-live="polite" style="font-size: 12px; color: #94a3b8;">
          <div style="color: #34d399;">Intent ${this.options.escapeHTML(String(intentId))} · PENDING_PAYMENT</div>
        </div>

        <!-- STEP 2 FOOTER -->
        <div style="display: flex; justify-content: flex-end; gap: 10px;">
          <button id="cashier-step2-done" style="
            padding: 10px 24px;
            background: #10b981;
            color: #020617;
            font-weight: 800;
            border: none;
            border-radius: 8px;
            cursor: pointer;
          ">
            Done / Return to Terminal ✓
          </button>
        </div>
      </div>
    `;
  }

  // ==========================================
  // WITHDRAWAL BODY
  // ==========================================
  private renderWithdrawalBody(): string {
    const activeCurrency = this.options.appState?.getSelectedCurrency?.() || this.selectedCurrency || "USD";
    const liveBalObj = this.options.appState?.getLiveBalance?.();
    const liveBalVal = liveBalObj ? Number(liveBalObj.available_balance || 0) : 0;
    const currSym = getCurrencySymbol(liveBalObj?.currency || activeCurrency);
    const kycTier = this.options.currentProfile?.kyc_tier || 2;

    const summary = this.walletSummary;
    const hasActiveBonus = Boolean(summary?.has_active_bonus);
    const realBalVal = summary ? Number(summary.real_available_balance ?? liveBalVal) : liveBalVal;
    const realBalFormatted = formatSmartBalance(realBalVal);
    const bonusBalVal = summary ? Number(summary.bonus_balance || 0) : 0;
    const bonusBalFormatted = formatSmartBalance(bonusBalVal);
    const totalEquityVal = summary ? Number(summary.total_trading_equity || (realBalVal + bonusBalVal)) : realBalVal;
    const totalEquityFormatted = formatSmartBalance(totalEquityVal);

    const wager = summary?.active_wager || {};
    const reqTurnFormatted = formatSmartBalance(Number(wager.required_turnover || 0));
    const compTurnFormatted = formatSmartBalance(Number(wager.completed_turnover || 0));
    const remTurnFormatted = formatSmartBalance(Number(wager.remaining_turnover || 0));
    const progPct = Number(wager.progress_percentage || 0).toFixed(1);
    const campName = wager.campaign_name || "Promotional Deposit Match";

    const bannerHtml = hasActiveBonus ? `
      <!-- DUAL BALANCE SEGREGATION & LIVE WAGER TRACKER -->
      <div style="
        background: linear-gradient(135deg, rgba(15, 23, 42, 0.98), rgba(24, 34, 53, 0.98));
        border: 1px solid rgba(245, 158, 11, 0.35);
        border-radius: 12px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 14px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
      ">
        <!-- Dual Balance Row -->
        <div style="display: flex; gap: 12px; flex-wrap: wrap;">
          <div style="flex: 1; min-width: 170px; background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.28); border-radius: 10px; padding: 12px 14px;">
            <div style="font-size: 11px; text-transform: uppercase; color: #34d399; font-weight: 800; display: flex; align-items: center; gap: 6px;">
              <span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:#10b981;box-shadow:0 0 6px #10b981;"></span>
              Available to Withdraw (Real Funds)
            </div>
            <div style="font-size: 22px; font-weight: 900; color: #ffffff; margin-top: 4px; font-family: monospace;">
              ${currSym}${realBalFormatted}
            </div>
            <div style="font-size: 11px; color: #10b981; margin-top: 3px; font-weight: 600;">✓ 100% Unrestricted Real Money</div>
          </div>

          <div style="flex: 1; min-width: 170px; background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.28); border-radius: 10px; padding: 12px 14px;">
            <div style="font-size: 11px; text-transform: uppercase; color: #fbbf24; font-weight: 800; display: flex; align-items: center; gap: 6px;">
              <span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:#f59e0b;box-shadow:0 0 6px #f59e0b;"></span>
              Bonus Balance (In Wagering)
            </div>
            <div style="font-size: 22px; font-weight: 900; color: #fbbf24; margin-top: 4px; font-family: monospace;">
              ${currSym}${bonusBalFormatted}
            </div>
            <div style="font-size: 11px; color: #94a3b8; margin-top: 3px;">⏳ Unlocks to Real Cash on Wager</div>
          </div>
        </div>

        <!-- Live Wagering Progress Box -->
        <div style="background: rgba(0, 0, 0, 0.35); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 8px; padding: 12px 14px;">
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px; margin-bottom: 6px;">
            <span style="color: #cbd5e1; font-weight: 700;">
              📊 ${campName}: <strong style="color: #f59e0b;">${currSym}${compTurnFormatted}</strong> / ${currSym}${reqTurnFormatted}
            </span>
            <span style="color: #f59e0b; font-weight: 800; font-family: monospace;">${progPct}%</span>
          </div>

          <!-- Progress Bar -->
          <div style="width: 100%; height: 7px; background: #1e293b; border-radius: 999px; overflow: hidden; margin-bottom: 8px;">
            <div style="width: ${progPct}%; height: 100%; background: linear-gradient(90deg, #f59e0b, #10b981); border-radius: 999px; transition: width 0.3s ease;"></div>
          </div>

          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #94a3b8; flex-wrap: wrap; gap: 8px;">
            <span>Remaining turnover: <strong style="color: #ffffff;">${currSym}${remTurnFormatted}</strong></span>
            <button type="button" id="btn-cancel-bonus-wager" style="background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.4); color: #fca5a5; font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 6px; cursor: pointer; transition: all 0.2s;">
              ✕ Cancel Bonus & Forfeit Wager
            </button>
          </div>
        </div>
      </div>
    ` : `
      <!-- STANDARD WITHDRAWAL ELIGIBILITY BANNER -->
      <div style="
        background: #080d16;
        border: 1px solid #1e293b;
        border-radius: 12px;
        padding: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      ">
        <div>
          <div style="font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 700;">Authoritative Balance (${activeCurrency})</div>
          <div style="font-size: 20px; font-weight: 800; color: #ffffff; margin-top: 2px;">${currSym}${realBalFormatted}</div>
          <div style="font-size: 11px; color: #10b981; margin-top: 2px;">✓ 100% Available for Fast Payout</div>
        </div>
        <div style="
          background: rgba(16, 185, 129, 0.15);
          border: 1px solid rgba(16, 185, 129, 0.3);
          padding: 8px 14px;
          border-radius: 8px;
          text-align: right;
        ">
          <span style="font-size: 10px; text-transform: uppercase; color: #34d399; font-weight: 700;">KYC Status</span>
          <div style="font-size: 13px; font-weight: 800; color: #ffffff;">Tier ${kycTier} Verified</div>
        </div>
      </div>
    `;

    const activeCurrRate = getLiveUsdFxRate(activeCurrency);
    const initialRateText = activeCurrency === "USD"
      ? "1 USDT = $1.00 USD (1:1 Direct Settlement)"
      : activeCurrency === "INR"
        ? `1 USDT = ₹${getLiveUsdInrRate().toFixed(2)} INR`
        : `1 USDT = ${currSym}${activeCurrRate.toFixed(2)} ${activeCurrency}`;

    return `
      <section style="display: flex; flex-direction: column; gap: 18px;">
        ${bannerHtml}

        <!-- WITHDRAWAL FORM -->
        <div style="
          background: #0f1624;
          border: 1px solid #1e293b;
          border-radius: 12px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        ">
          <div>
            <label style="display: block; font-size: 12px; font-weight: 700; color: #94a3b8; margin-bottom: 6px;">Payment Method / Payout Rail</label>
            <select id="cashier-network" style="
              width: 100%;
              padding: 10px 12px;
              background: #080d16;
              border: 1px solid #334155;
              border-radius: 8px;
              color: #ffffff;
              font-size: 13px;
            ">
              <option value="USDT_TRC20" selected>USDT (TRC-20 Tron) · ⚡ Fast (< 15 mins)</option>
              <option value="USDT_BEP20">USDT (BEP-20 BNB Smart Chain) · ⚡ Instant</option>
              <option value="BTC">Bitcoin (BTC Native Core)</option>
              <option value="ETH">Ethereum (ETH ERC-20)</option>
              <option value="TON">Toncoin (TON Mainnet / USDT TON)</option>
              <option value="USDC">USD Coin (USDC)</option>
              ${activeCurrency === "INR" ? `
                <option value="UPI">UPI Instant (PhonePe, Google Pay, Paytm, BHIM)</option>
                <option value="IMPS">Bank Wire / IMPS (Direct Bank 24/7)</option>
              ` : ''}
            </select>
          </div>

          <div>
            <label id="cashier-amount-label" style="display: block; font-size: 12px; font-weight: 700; color: #94a3b8; margin-bottom: 6px;">Withdrawal Amount (${currSym} ${activeCurrency})</label>
            <input
              id="cashier-amount"
              inputmode="decimal"
              placeholder="e.g. ${liveBalVal > 0 ? (liveBalVal * 0.5).toFixed(2) : '500.00'}"
              style="
                box-sizing: border-box;
                width: 100%;
                padding: 10px 12px;
                background: #080d16;
                border: 1px solid #334155;
                border-radius: 8px;
                color: #ffffff;
                font-size: 15px;
                font-weight: 700;
              "
            />
            <div id="cashier-balance-warning" style="display:none;margin-top:8px;padding:10px 12px;background:rgba(239,68,68,0.15);border:1px solid rgba(239,68,68,0.4);border-radius:8px;color:#fca5a5;font-size:12px;font-weight:600;line-height:1.4;">
            </div>
            <div style="display: flex; gap: 8px; margin-top: 8px;">
              <button type="button" class="wdr-pct" data-pct="25" style="flex: 1; padding: 6px; background: #1e293b; border: 1px solid #334155; color: #cbd5e1; border-radius: 6px; font-size: 11px; cursor: pointer;">25%</button>
              <button type="button" class="wdr-pct" data-pct="50" style="flex: 1; padding: 6px; background: #1e293b; border: 1px solid #334155; color: #cbd5e1; border-radius: 6px; font-size: 11px; cursor: pointer;">50%</button>
              <button type="button" class="wdr-pct" data-pct="75" style="flex: 1; padding: 6px; background: #1e293b; border: 1px solid #334155; color: #cbd5e1; border-radius: 6px; font-size: 11px; cursor: pointer;">75%</button>
              <button type="button" class="wdr-pct" data-pct="100" style="flex: 1; padding: 6px; background: #1e293b; border: 1px solid #334155; color: #38bdf8; border-radius: 6px; font-size: 11px; font-weight: 700; cursor: pointer;">100% (MAX)</button>
            </div>
          </div>

          <!-- ESTIMATED NET SETTLEMENT PREVIEW BOX (PLATFORM SPONSORED GAS) -->
          <div id="cashier-withdrawal-conversion" style="
            background: linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%);
            border: 1px solid rgba(56, 189, 248, 0.3);
            border-radius: 8px;
            padding: 10px 12px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 11px;
          ">
            <div>
              <span style="color: #94a3b8; font-size: 9.5px; text-transform: uppercase; font-weight: 700; display: block;">Settlement Summary</span>
              <strong id="wdr-conversion-rate" style="color: #38bdf8;">${initialRateText}</strong>
              <div style="font-size: 9.5px; color: #10b981; margin-top: 2px;">⚡ Network Gas Fee: Platform Sponsored (0% deduction)</div>
            </div>
            <div style="text-align: right;">
              <span style="color: #94a3b8; font-size: 9.5px; text-transform: uppercase; font-weight: 700; display: block;">Estimated Net Settlement</span>
              <strong id="wdr-net-settlement" style="font-size: 13.5px; color: #34d399;">0.00 USDT</strong>
            </div>
          </div>

          <div>
            <label id="cashier-destination-label" style="display: block; font-size: 12px; font-weight: 700; color: #94a3b8; margin-bottom: 6px;">${activeCurrency === "INR" ? "Destination UPI ID / Bank Account Details" : "Destination Address / UPI ID / IBAN"}</label>
            <input
              id="cashier-destination"
              autocomplete="off"
              placeholder="${activeCurrency === "INR" ? "Enter your UPI ID (e.g. name@okhdfcbank) or Account Number & IFSC" : "Paste your recipient address or UPI ID"}"
              style="
                box-sizing: border-box;
                width: 100%;
                padding: 10px 12px;
                background: #080d16;
                border: 1px solid #334155;
                border-radius: 8px;
                color: #ffffff;
                font-family: monospace;
                font-size: 13px;
              "
            />
          </div>

          <div id="cashier-result" aria-live="polite" style="font-size: 12px; color: #94a3b8;"></div>

          <div style="
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-top: 10px;
            padding-top: 14px;
            border-top: 1px solid #1e293b;
          ">
            <div style="font-size: 11px; color: #94a3b8; background: rgba(56, 189, 248, 0.08); border: 1px solid rgba(56, 189, 248, 0.2); padding: 10px 12px; border-radius: 8px; margin-bottom: 12px; display: flex; align-items: flex-start; gap: 8px; text-align: left; width: 100%;">
              <span>🛡️</span>
              <span><strong>Promotional Turnover Policy:</strong> Withdrawing funds before fulfilling wagering turnover will automatically forfeit unvested promotional bonus credit, keeping your real deposited capital freely accessible.</span>
            </div>
            <span style="font-size: 11px; color: #64748b;">
              🔒 Locked under Tier 2 Compliance Safeguards
            </span>
            <button id="cashier-submit" style="
              padding: 10px 24px;
              background: #0284c7;
              color: #ffffff;
              font-weight: 700;
              border: none;
              border-radius: 8px;
              font-size: 14px;
              cursor: pointer;
            ">
              Submit Withdrawal Request
            </button>
          </div>
        </div>
      </section>
    `;
  }

  // ==========================================
  // TRANSACTION HISTORY BODY
  // ==========================================
  private renderHistoryBody(): string {
    const rowsHtml = this.isLoadingHistory
      ? `
        <tr>
          <td colspan="5" style="padding: 36px 14px; text-align: center; color: #94a3b8;">
            <div style="font-size: 14px; font-weight: 600;">Loading authoritative cashier records...</div>
          </td>
        </tr>
      `
      : (this.cashierTransactions && this.cashierTransactions.length > 0)
        ? this.cashierTransactions.map((tx: any) => {
            const isDep = String(tx.type || tx.tx_type || "").toUpperCase() === "DEPOSIT";
            const typeColor = isDep ? "#10b981" : "#38bdf8";
            const typeBg = isDep ? "rgba(16, 185, 129, 0.15)" : "rgba(56, 189, 248, 0.15)";
            const curr = String(tx.currency || "USD").toUpperCase();
            const sym = curr === "INR" ? "₹" : (curr === "EUR" ? "€" : (curr === "GBP" ? "£" : "$"));
            const amtStr = `${sym}${parseFloat(tx.amount || "0").toFixed(2)} ${curr}`;
            const methodStr = tx.payment_method || (isDep ? "Deposit Rail" : "Withdrawal Rail");
            const refStr = tx.tx_hash || tx.destination_address || tx.deposit_address || tx.id || "";
            const shortRef = refStr.length > 18 ? `${refStr.slice(0, 8)}...${refStr.slice(-6)}` : (refStr || "—");
            const status = String(tx.status || "PENDING").toUpperCase();
            const isSuccess = status === "COMPLETED" || status === "APPROVED" || status === "CONFIRMED";
            const isPending = status.includes("PENDING") || status.includes("PROPOSED") || status.includes("AWAITING");
            const statusColor = isSuccess ? "#34d399" : (isPending ? "#fbbf24" : "#fb7185");
            const statusBg = isSuccess ? "rgba(16, 185, 129, 0.2)" : (isPending ? "rgba(245, 158, 11, 0.2)" : "rgba(244, 63, 94, 0.2)");
            const dateStr = tx.created_at ? new Date(tx.created_at).toLocaleString() : "Recent";

            return `
              <tr style="border-bottom: 1px solid #1e293b; transition: background 0.15s ease;">
                <td style="padding: 12px 14px;">
                  <span style="background: ${typeBg}; color: ${typeColor}; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 800; letter-spacing: 0.5px;">${isDep ? "DEPOSIT" : "WITHDRAWAL"}</span>
                  <div style="font-size: 10px; color: #64748b; margin-top: 4px;">${this.options.escapeHTML(dateStr)}</div>
                </td>
                <td style="padding: 12px 14px; font-weight: 700; font-family: monospace; font-size: 13px; color: #f8fafc;">
                  ${this.options.escapeHTML(amtStr)}
                </td>
                <td style="padding: 12px 14px; color: #cbd5e1; font-weight: 500;">
                  ${this.options.escapeHTML(methodStr)}
                </td>
                <td style="padding: 12px 14px; font-family: monospace; color: #38bdf8;">
                  <div style="display: flex; align-items: center; gap: 6px;">
                    <span title="${this.options.escapeHTML(refStr)}">${this.options.escapeHTML(shortRef)}</span>
                    ${refStr ? `<button type="button" class="btn-copy-history-ref" data-copy="${this.options.escapeHTML(refStr)}" style="background: rgba(56, 189, 248, 0.12); border: 1px solid rgba(56, 189, 248, 0.3); color: #38bdf8; border-radius: 4px; padding: 2px 6px; font-size: 10px; cursor: pointer;" title="Copy to clipboard">📋</button>` : ''}
                  </div>
                </td>
                <td style="padding: 12px 14px;">
                  <span style="background: ${statusBg}; color: ${statusColor}; padding: 3px 10px; border-radius: 12px; font-size: 10.5px; font-weight: 700; text-transform: uppercase;">${this.options.escapeHTML(status)}</span>
                </td>
              </tr>
            `;
          }).join("")
        : `
          <tr>
            <td colspan="5" style="padding: 36px 14px; text-align: center; color: #64748b;">
              <div style="font-size: 24px; margin-bottom: 8px;">📂</div>
              <div style="font-size: 13px; font-weight: 600; color: #94a3b8;">No transaction history yet</div>
              <div style="font-size: 11px; margin-top: 4px;">Completed deposits and withdrawal requests will appear here in real time.</div>
            </td>
          </tr>
        `;

    return `
      <section style="display: flex; flex-direction: column; gap: 14px;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <h4 style="margin: 0; font-size: 14px; color: #ffffff;">Recent Cashier Ingestion & Payout Records</h4>
          <span style="font-size: 11px; color: #64748b;">Live Node Synchronized</span>
        </div>

        <div style="
          background: #080d16;
          border: 1px solid #1e293b;
          border-radius: 10px;
          overflow: hidden;
        ">
          <table style="width: 100%; border-collapse: collapse; font-size: 12px; text-align: left;">
            <thead>
              <tr style="background: #0f1624; color: #64748b; border-bottom: 1px solid #1e293b;">
                <th style="padding: 10px 14px;">Type</th>
                <th style="padding: 10px 14px;">Amount</th>
                <th style="padding: 10px 14px;">Method / Rail</th>
                <th style="padding: 10px 14px;">TxID / Destination</th>
                <th style="padding: 10px 14px;">Status</th>
              </tr>
            </thead>
            <tbody id="cashier-history-rows">
              ${rowsHtml}
            </tbody>
          </table>
        </div>
      </section>
    `;
  }

  private updateDepositCalculations() {
    const input = document.getElementById("cashier-amount") as HTMLInputElement | null;
    const val = input ? parseFloat(input.value) : this.selectedAmount;
    const validAmount = !isNaN(val) && val > 0 ? val : 0;
    this.selectedAmount = validAmount;

    // 1. Update FX banner
    const banner = document.getElementById("cashier-fx-banner");
    if (banner && banner.parentElement) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = this.renderCryptoBannerHtml(validAmount).trim();
      const newBanner = tempDiv.firstElementChild;
      if (newBanner) {
        banner.replaceWith(newBanner);
      }
    }

    // 2. Update equity display
    const liveRate = getLiveUsdInrRate();
    const isTargetINR = this.targetAccountCurrency === "INR";
    const currSymbol = isTargetINR ? "₹" : "$";
    const targetDepEquity = isTargetINR
      ? (this.selectedCurrency === "INR" ? validAmount : validAmount * liveRate)
      : (this.selectedCurrency === "INR" ? validAmount / liveRate : validAmount);
    const selectedBonus = this.getCompatibleBonuses().find((b) => String(b.campaign_id) === this.selectedBonusId);
    const matchMultiplier = selectedBonus ? (Number(selectedBonus.match_percentage_bps || 0) / 10000) : 0;
    const depositBonusAmt = (this.userDeclinedBonus || !this.selectedBonusId) ? 0 : (targetDepEquity * matchMultiplier);
    const totalTradingPower = (targetDepEquity + depositBonusAmt).toFixed(2);

    const depEquityEl = document.getElementById("cashier-deposit-equity-val");
    if (depEquityEl) depEquityEl.textContent = `${currSymbol}${targetDepEquity.toFixed(2)}`;

    const bonusEquityEl = document.getElementById("cashier-bonus-equity-val");
    if (bonusEquityEl) bonusEquityEl.textContent = `+${currSymbol}${depositBonusAmt.toFixed(2)}`;

    const totalEquityEl = document.getElementById("cashier-total-equity-val");
    if (totalEquityEl) totalEquityEl.textContent = `${currSymbol}${totalTradingPower}`;

    // 3. Highlight preset chip if matching
    document.querySelectorAll(".amount-chip").forEach((chip) => {
      const chipAmt = Number(chip.getAttribute("data-amount"));
      if (chipAmt === validAmount) {
        chip.classList.add("active");
        (chip as HTMLElement).style.borderColor = "#38bdf8";
        (chip as HTMLElement).style.background = "rgba(56, 189, 248, 0.15)";
      } else {
        chip.classList.remove("active");
        (chip as HTMLElement).style.borderColor = "#334155";
        (chip as HTMLElement).style.background = "#080d16";
      }
    });
  }

  // ==========================================
  // EVENT LISTENERS & INTERACTION
  // ==========================================
  private attachEventListeners() {
    // Mode Switcher Tabs
    document.getElementById("tab-deposit")?.addEventListener("click", () => {
      this.currentMode = "DEPOSIT";
      this.activeStep = 1;
      this.render();
    });
    document.getElementById("tab-withdraw")?.addEventListener("click", async () => {
      this.currentMode = "WITHDRAWAL";
      const activeCurr = this.options.appState?.getSelectedCurrency?.() || this.selectedCurrency || "USD";
      this.walletSummary = await this.options.restClient.getWalletSummary(activeCurr).catch(() => null);
      this.render();
    });
    document.getElementById("tab-history")?.addEventListener("click", async () => {
      this.currentMode = "HISTORY";
      this.isLoadingHistory = true;
      this.render();
      try {
        const txRes = await this.options.restClient.getCashierTransactions().catch(() => ({ transactions: [] }));
        this.cashierTransactions = txRes?.transactions || [];
      } catch {
        this.cashierTransactions = [];
      } finally {
        this.isLoadingHistory = false;
        this.render();
      }
    });

    // History Copy Button Listener
    document.querySelectorAll(".btn-copy-history-ref").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const copyVal = (btn as HTMLElement).getAttribute("data-copy") || "";
        if (copyVal) {
          navigator.clipboard.writeText(copyVal).then(() => {
            this.options.showDashboardNotice("Copied to clipboard: " + (copyVal.length > 20 ? copyVal.slice(0, 18) + "..." : copyVal), "success");
          });
        }
      });
    });

    // Close Modal
    document.getElementById("cashier-close")?.addEventListener("click", () => this.close());
    document.getElementById("cashier-cancel-btn")?.addEventListener("click", () => this.close());
    document.getElementById("cashier-step2-done")?.addEventListener("click", () => this.close());

    // Target Wallet Selector Buttons (INR Account vs USD Account)
    document.querySelectorAll(".btn-target-wallet").forEach((btn) => {
      btn.addEventListener("click", () => {
        const target = (btn.getAttribute("data-target") || "INR").toUpperCase();
        this.targetAccountCurrency = target;
        this.selectedCurrency = target === "INR" ? "INR" : "USD";
        this.selectedAmount = target === "INR" ? 5000 : 100;
        this.syncSelectedBonus();
        this.render();
      });
    });

    // Category Tabs in Step 1
    document.getElementById("cat-upi")?.addEventListener("click", () => {
      this.currentCategory = "UPI";
      this.selectedCurrency = "INR";
      this.selectedMethod = DEFAULT_METHODS.find((m) => m.category === "UPI") || DEFAULT_METHODS[0];
      this.selectedAmount = 5000;
      this.syncSelectedBonus();
      this.render();
    });
    document.getElementById("cat-crypto")?.addEventListener("click", () => {
      this.currentCategory = "CRYPTO";
      if (this.targetAccountCurrency === "INR") {
        this.selectedCurrency = "INR";
        if (this.selectedAmount < 500) this.selectedAmount = 5000;
      } else {
        this.selectedCurrency = "USD";
        if (this.selectedAmount > 5000) this.selectedAmount = 100;
      }
      this.selectedMethod = DEFAULT_METHODS.find((m) => m.category === "CRYPTO") || DEFAULT_METHODS[5];
      this.syncSelectedBonus();
      this.render();
    });
    document.getElementById("cat-fiat")?.addEventListener("click", () => {
      this.currentCategory = "FIAT";
      this.selectedCurrency = "USD";
      this.selectedMethod = DEFAULT_METHODS.find((m) => m.category === "FIAT") || DEFAULT_METHODS[11];
      this.selectedAmount = 100;
      this.syncSelectedBonus();
      this.render();
    });

    // Payment Method Cards
    document.querySelectorAll(".method-card").forEach((card) => {
      card.addEventListener("click", () => {
        const id = card.getAttribute("data-method-id");
        const found = this.getAllAvailableMethods().find((m) => m.id === id);
        if (found) {
          this.selectedMethod = found;
          if (found.category === "UPI") {
            this.selectedCurrency = "INR";
            if (this.selectedAmount < 500) this.selectedAmount = 5000;
          } else if (found.category === "CRYPTO") {
            if (this.targetAccountCurrency === "INR") {
              this.selectedCurrency = "INR";
              if (this.selectedAmount < 500) this.selectedAmount = 5000;
            } else {
              this.selectedCurrency = "USD";
              if (this.selectedAmount > 5000) this.selectedAmount = 100;
            }
          }
          this.syncSelectedBonus();
          this.render();
        }
      });
    });

    // Currency Switcher Buttons
    document.getElementById("curr-usd")?.addEventListener("click", () => {
      this.selectedCurrency = "USD";
      this.selectedAmount = 100;
      this.syncSelectedBonus();
      this.render();
    });
    document.getElementById("curr-inr")?.addEventListener("click", () => {
      this.selectedCurrency = "INR";
      this.selectedAmount = 5000;
      this.syncSelectedBonus();
      this.render();
    });

    // Amount Chips
    document.querySelectorAll(".amount-chip").forEach((chip) => {
      chip.addEventListener("click", () => {
        const amt = Number(chip.getAttribute("data-amount"));
        if (!isNaN(amt) && amt > 0) {
          this.selectedAmount = amt;
          const input = document.getElementById("cashier-amount") as HTMLInputElement | null;
          if (input) input.value = String(amt);
          this.updateDepositCalculations();
        }
      });
    });

    // Amount Input Keyup / Change (Real-time dynamic recalculation)
    const amountInput = document.getElementById("cashier-amount") as HTMLInputElement | null;
    if (amountInput) {
      amountInput.addEventListener("input", () => {
        this.updateDepositCalculations();
      });
    }

    // Bonus Radio Cards Change (Synced with Mobile PWA & Dropdown)
    const overlayEl = document.getElementById("modal-overlay") || document;

    const syncBonusSelectionState = (val: string) => {
      const isNone = !val || val === "none";
      this.userDeclinedBonus = isNone;
      this.selectedBonusId = isNone ? "" : val;

      overlayEl.querySelectorAll('.desktop-bonus-option-card').forEach((card) => {
        const radio = card.querySelector('input[name="desktop-bonus-campaign"]') as HTMLInputElement | null;
        if (!radio) return;
        const matches = isNone ? (!radio.value || radio.value === "none") : (radio.value === this.selectedBonusId);
        radio.checked = matches;
        const isNoBonusCard = card.classList.contains('no-bonus-card');

        if (matches) {
          card.classList.add('active');
          if (isNoBonusCard) {
            (card as HTMLElement).style.setProperty('border', '1.5px solid #38bdf8', 'important');
            (card as HTMLElement).style.setProperty('background', 'rgba(56, 189, 248, 0.08)', 'important');
            (card as HTMLElement).style.setProperty('box-shadow', '0 0 12px rgba(56, 189, 248, 0.15)', 'important');
          } else {
            (card as HTMLElement).style.setProperty('border', '1.5px solid #38bdf8', 'important');
            (card as HTMLElement).style.setProperty('background', 'linear-gradient(135deg, rgba(14, 165, 233, 0.16) 0%, rgba(56, 189, 248, 0.08) 100%)', 'important');
            (card as HTMLElement).style.setProperty('box-shadow', '0 0 12px rgba(56, 189, 248, 0.15)', 'important');
          }
          const title = card.querySelector('.bonus-title-val') as HTMLElement | null;
          if (title) title.style.color = '#ffffff';
        } else {
          card.classList.remove('active');
          if (isNoBonusCard) {
            (card as HTMLElement).style.setProperty('border', '1.5px dashed #1e293b', 'important');
            (card as HTMLElement).style.setProperty('background', '#0a0f18', 'important');
            (card as HTMLElement).style.setProperty('box-shadow', 'none', 'important');
          } else {
            (card as HTMLElement).style.setProperty('border', '1.5px solid #1e293b', 'important');
            (card as HTMLElement).style.setProperty('background', '#0a0f18', 'important');
            (card as HTMLElement).style.setProperty('box-shadow', 'none', 'important');
          }
          const title = card.querySelector('.bonus-title-val') as HTMLElement | null;
          if (title) title.style.color = isNoBonusCard ? '#94a3b8' : '#e2e8f0';
        }
      });

      const bSelect = document.getElementById("cashier-bonus") as HTMLSelectElement | null;
      if (bSelect) bSelect.value = this.selectedBonusId;

      this.updateDepositCalculations();
    };

    overlayEl.querySelectorAll('input[name="desktop-bonus-campaign"]').forEach((radio) => {
      radio.addEventListener("change", (e) => {
        syncBonusSelectionState((e.target as HTMLInputElement).value);
      });
      radio.addEventListener("click", (e) => {
        syncBonusSelectionState((e.target as HTMLInputElement).value);
      });
    });

    overlayEl.querySelectorAll('.desktop-bonus-option-card').forEach((card) => {
      card.addEventListener('click', () => {
        const radio = card.querySelector('input[name="desktop-bonus-campaign"]') as HTMLInputElement | null;
        if (radio) {
          syncBonusSelectionState(radio.value);
        }
      });
    });

    // Bonus Dropdown Change
    const bonusSelect = document.getElementById("cashier-bonus") as HTMLSelectElement | null;
    if (bonusSelect) {
      bonusSelect.addEventListener("change", (e) => {
        const val = (e.target as HTMLSelectElement).value;
        if (!val || val === "none") {
          this.userDeclinedBonus = true;
          this.selectedBonusId = "";
        } else {
          this.userDeclinedBonus = false;
          this.selectedBonusId = val;
        }
        this.render();
      });
    }

    // Claim Prior Bonus Button
    document.getElementById("claim-existing-deposit-bonus")?.addEventListener("click", async () => {
      const status = document.getElementById("cashier-bonus-status");
      const campaignId = (document.getElementById("cashier-bonus") as HTMLSelectElement)?.value || this.selectedBonusId;
      if (!campaignId) {
        if (status) status.textContent = "Choose a bonus campaign first.";
        return;
      }
      try {
        const grant = await this.options.restClient.claimBonus(campaignId);
        if (status) status.textContent = `Bonus $${(Number(grant.granted_amount_cents || 0) / 100).toFixed(2)} credited to LIVE bonus balance.`;
        try {
          localStorage.setItem("starx_first_deposit_banner_dismissed", "true");
        } catch {}
        document.getElementById("first-deposit-promo")?.classList.remove("show");
        if (typeof BroadcastChannel !== "undefined") {
          try {
            const bc = new BroadcastChannel("starx_rewards_sync");
            bc.postMessage({ type: "BALANCE_UPDATED", grant });
            bc.close();
          } catch {}
        }
        await this.options.appState.refreshLiveState();
        this.options.renderHeader();
        void this.options.loadFirstDepositPromotion();
      } catch (error: any) {
        if (status) status.textContent = error?.message || "Bonus claim failed.";
      }
    });

    // Step 2 Back Button
    document.getElementById("btn-back-step1")?.addEventListener("click", () => {
      if (this.countdownTimerInterval) {
        clearInterval(this.countdownTimerInterval);
        this.countdownTimerInterval = null;
      }
      this.activeStep = 1;
      this.render();
    });

    // 1-Click Copy Address / VPA Button
    document.getElementById("btn-copy-address")?.addEventListener("click", () => {
      const input = document.getElementById("checkout-addr-display") as HTMLInputElement | null;
      if (input) {
        navigator.clipboard?.writeText(input.value);
        const btn = document.getElementById("btn-copy-address");
        if (btn) {
          btn.innerHTML = `<span style="color:#10b981">✓ Copied!</span>`;
          setTimeout(() => {
            if (btn) btn.innerHTML = `${BRAND_ICONS.copy} Copy`;
          }, 2000);
        }
      }
    });

    // 1-Click Copy Amount Button
    document.getElementById("btn-copy-amount")?.addEventListener("click", () => {
      const btn = document.getElementById("btn-copy-amount") as HTMLButtonElement | null;
      const amt = btn?.getAttribute("data-amt");
      if (amt) {
        navigator.clipboard?.writeText(amt);
        if (btn) {
          btn.innerHTML = `<span style="color:#10b981">✓ Copied!</span>`;
          setTimeout(() => {
            if (btn) btn.innerHTML = `${BRAND_ICONS.copy} Copy`;
          }, 2000);
        }
      }
    });

    // Submit Proof Button (Step 2)
    bindDepositProof({
      input: document.getElementById("deposit-transaction-hash") as HTMLInputElement | null,
      button: document.getElementById("submit-deposit-proof") as HTMLButtonElement | null,
      status: document.getElementById("deposit-proof-status"),
      depositId: () => String(this.currentDepositResponse?.deposit_id || ""),
      isUPI: () => this.selectedMethod.category === "UPI",
      submit: (id, reference) => this.options.restClient.submitDepositProof(id, reference),
      refresh: async () => {
        await this.options.appState.refreshLiveState();
        this.options.renderHeader();
      },
    });

    // Helper to calculate maximum withdrawable amount in chosen payout currency
    const getWithdrawalLimits = (payoutCurr: string) => {
      const liveBalObj = this.options.appState?.getLiveBalance?.();
      const rawBal = liveBalObj ? Number(liveBalObj.available_balance || 0) : 0;
      const balCurr = (liveBalObj?.currency || this.options.appState?.getSelectedCurrency?.() || "USD").toUpperCase();
      const balSym = getCurrencySymbol(balCurr);
      const payoutSym = getCurrencySymbol(payoutCurr);
      let maxWithdrawable = rawBal;
      if (balCurr === "INR" && (payoutCurr === "USD" || payoutCurr === "USDT" || payoutCurr === "USDC")) {
        maxWithdrawable = rawBal / USD_TO_INR_RATE;
      } else if ((balCurr === "USD" || balCurr === "USDT" || balCurr === "USDC") && payoutCurr === "INR") {
        maxWithdrawable = rawBal * USD_TO_INR_RATE;
      }
      return { rawBal, balCurr, balSym, maxWithdrawable, payoutSym };
    };

    const updateWithdrawalValidation = (): boolean => {
      const amtInput = document.getElementById("cashier-amount") as HTMLInputElement | null;
      const netSelect = document.getElementById("cashier-network") as HTMLSelectElement | null;
      const warningBox = document.getElementById("cashier-balance-warning");
      const conversionRateEl = document.getElementById("wdr-conversion-rate");
      const netSettlementEl = document.getElementById("wdr-net-settlement");
      const destLabel = document.getElementById("cashier-destination-label");
      const destInput = document.getElementById("cashier-destination") as HTMLInputElement | null;
      if (!amtInput) return true;

      const liveBalObj = this.options.appState?.getLiveBalance?.();
      const rawBal = liveBalObj ? Number(liveBalObj.available_balance || 0) : 0;
      const activeCurr = (liveBalObj?.currency || this.options.appState?.getSelectedCurrency?.() || "USD").toUpperCase();
      const isINR = activeCurr === "INR";
      const sym = isINR ? "₹" : "$";
      const entered = parseFloat(amtInput.value.trim() || "0");
      const rail = (netSelect?.value || "USDT_TRC20").toUpperCase();
      const liveRate = getLiveUsdInrRate();

      // Update Destination Label and placeholder based on rail
      if (destLabel && destInput) {
        if (rail.includes("TRC20")) {
          destLabel.textContent = "Destination USDT (TRC-20) Wallet Address";
          destInput.placeholder = "Paste your TRC-20 wallet address (starts with T...)";
        } else if (rail.includes("BEP20") || rail === "ETH") {
          destLabel.textContent = `Destination ${rail} EVM Wallet Address`;
          destInput.placeholder = "Paste your 0x EVM/BSC wallet address";
        } else if (rail === "BTC") {
          destLabel.textContent = "Destination Bitcoin (BTC) Wallet Address";
          destInput.placeholder = "Paste your Bitcoin address (bc1..., 1..., or 3...)";
        } else if (rail === "TON") {
          destLabel.textContent = "Destination Toncoin (TON) Wallet Address";
          destInput.placeholder = "Paste your TON wallet address (UQ... or EQ...)";
        } else if (rail === "USDC") {
          destLabel.textContent = "Destination USDC Wallet Address";
          destInput.placeholder = "Paste your USDC wallet address";
        } else if (rail === "UPI") {
          destLabel.textContent = "Destination UPI ID (VPA)";
          destInput.placeholder = "Enter your UPI ID (e.g. name@okhdfcbank)";
        } else if (rail === "IMPS") {
          destLabel.textContent = "Destination Bank Account Number & IFSC";
          destInput.placeholder = "Enter Account Number,IFSC (e.g. 1234567890,HDFC0001234)";
        }
      }

      // Calculate Net Settlement & Rate text for ANY world currency
      if (conversionRateEl && netSettlementEl) {
        const currFxRate = getLiveUsdFxRate(activeCurr); // 1 USD = currFxRate local units
        const usdAmt = isINR ? (entered / liveRate) : (activeCurr === "USD" ? entered : (currFxRate > 0 ? entered / currFxRate : entered));
        const currSym = getCurrencySymbol(activeCurr);

        if (rail === "UPI" || rail === "IMPS") {
          conversionRateEl.textContent = "Direct 1:1 INR Settlement";
          netSettlementEl.textContent = `₹${Math.round(isINR ? entered : entered * liveRate).toLocaleString()} INR`;
        } else if (rail.includes("TRC20") || rail.includes("BEP20")) {
          if (activeCurr === "USD") {
            conversionRateEl.textContent = "1 USDT = $1.00 USD (1:1 Direct Settlement)";
          } else if (isINR) {
            conversionRateEl.textContent = `1 USDT = ₹${liveRate.toFixed(2)} INR`;
          } else {
            conversionRateEl.textContent = `1 USDT = ${currSym}${currFxRate.toFixed(2)} ${activeCurr}`;
          }
          netSettlementEl.textContent = `${usdAmt.toFixed(2)} USDT`;
        } else if (rail === "USDC") {
          if (activeCurr === "USD") {
            conversionRateEl.textContent = "1 USDC = $1.00 USD (1:1 Direct Settlement)";
          } else if (isINR) {
            conversionRateEl.textContent = `1 USDC = ₹${liveRate.toFixed(2)} INR`;
          } else {
            conversionRateEl.textContent = `1 USDC = ${currSym}${currFxRate.toFixed(2)} ${activeCurr}`;
          }
          netSettlementEl.textContent = `${usdAmt.toFixed(2)} USDC`;
        } else if (rail === "BTC") {
          const btcPrice = this.cryptoRates.BTC || 87500;
          const cryptoAmt = usdAmt / btcPrice;
          conversionRateEl.textContent = `1 BTC ≈ $${btcPrice >= 1000 ? Math.round(btcPrice).toLocaleString('en-US') : btcPrice.toFixed(2)} (${currSym}${Math.round(btcPrice * currFxRate).toLocaleString('en-US')})`;
          netSettlementEl.textContent = `${this.formatCryptoAmount(cryptoAmt, "BTC")} BTC`;
        } else if (rail === "ETH") {
          const ethPrice = this.cryptoRates.ETH || 2800;
          const cryptoAmt = usdAmt / ethPrice;
          conversionRateEl.textContent = `1 ETH ≈ $${ethPrice >= 1000 ? Math.round(ethPrice).toLocaleString('en-US') : ethPrice.toFixed(2)} (${currSym}${Math.round(ethPrice * currFxRate).toLocaleString('en-US')})`;
          netSettlementEl.textContent = `${this.formatCryptoAmount(cryptoAmt, "ETH")} ETH`;
        } else if (rail === "TON") {
          const tonPrice = this.cryptoRates.TON || 1.6;
          const cryptoAmt = usdAmt / tonPrice;
          conversionRateEl.textContent = `1 TON ≈ $${tonPrice.toFixed(2)} (${currSym}${Math.round(tonPrice * currFxRate)})`;
          netSettlementEl.textContent = `${this.formatCryptoAmount(cryptoAmt, "TON")} TON`;
        }
      }

      if (!isNaN(entered) && entered > rawBal) {
        if (warningBox) {
          warningBox.innerHTML = `⚠️ <strong>Insufficient balance:</strong> Requested amount (${sym}${entered.toFixed(2)} ${activeCurr}) exceeds your available balance of <strong>${sym}${formatSmartBalance(rawBal)} ${activeCurr}</strong>.`;
          warningBox.style.display = "block";
        }
        amtInput.style.borderColor = "#ef4444";
        return false;
      } else {
        if (warningBox) warningBox.style.display = "none";
        amtInput.style.borderColor = "#334155";
        return true;
      }
    };

    const netSelect = document.getElementById("cashier-network") as HTMLSelectElement | null;
    netSelect?.addEventListener("change", updateWithdrawalValidation);

    const amtInput = document.getElementById("cashier-amount") as HTMLInputElement | null;
    amtInput?.addEventListener("input", updateWithdrawalValidation);
    updateWithdrawalValidation();

    // Withdrawal Percentage Buttons
    document.querySelectorAll(".wdr-pct").forEach((btn) => {
      btn.addEventListener("click", () => {
        const pct = Number(btn.getAttribute("data-pct"));
        const liveBalObj = this.options.appState?.getLiveBalance?.();
        const rawBal = liveBalObj ? Number(liveBalObj.available_balance || 0) : 0;
        if (!isNaN(rawBal) && rawBal > 0) {
          const amt = ((rawBal * pct) / 100).toFixed(2);
          if (amtInput) {
            amtInput.value = amt;
            updateWithdrawalValidation();
          }
        }
      });
    });

    // Cancel Bonus & Forfeit Wager Button
    document.getElementById("btn-cancel-bonus-wager")?.addEventListener("click", async () => {
      const activeCurr = (this.options.appState?.getSelectedCurrency?.() || this.selectedCurrency || "USD").toUpperCase();
      const currSym = getCurrencySymbol(activeCurr);
      const bonusBal = this.walletSummary ? Number(this.walletSummary.bonus_balance || 0) : 0;
      const realBal = this.walletSummary ? Number(this.walletSummary.real_available_balance || 0) : 0;
      const confirmed = confirm(
        `Are you sure you want to cancel your ${currSym}${formatSmartBalance(bonusBal)} promotional bonus?\n\n` +
        `This will immediately clear all wagering turnover requirements. Your real balance of ${currSym}${formatSmartBalance(realBal)} will remain 100% unrestricted for trading and instant withdrawal.`
      );
      if (!confirmed) return;

      try {
        const res = await this.options.restClient.cancelBonus();
        this.options.showDashboardNotice(res.message || "Promotional bonus cancelled. Wagering restrictions cleared.", "success");
        this.walletSummary = await this.options.restClient.getWalletSummary(activeCurr).catch(() => null);
        this.options.renderHeader();
        this.render();
      } catch (err: any) {
        this.options.showDashboardNotice(err.message || "Could not cancel promotional bonus", "error");
      }
    });

    // Main Cashier Submit (Step 1 -> Step 2 OR Withdrawal)
    document.getElementById("cashier-submit")?.addEventListener("click", async () => {
      const amount = (document.getElementById("cashier-amount") as HTMLInputElement)?.value.trim() || String(this.selectedAmount);
      const network = (document.getElementById("cashier-network") as HTMLSelectElement)?.value || this.selectedMethod.id;
      const destination = (document.getElementById("cashier-destination") as HTMLInputElement)?.value.trim() || "";
      const result = document.getElementById("cashier-result");

      const isDeposit = this.currentMode === "DEPOSIT";

      if (!/^\d+(\.\d{1,8})?$/.test(amount) || Number(amount) <= 0 || (!isDeposit && destination.length < 5)) {
        if (result) {
          result.innerHTML = `<span style="color:#fb7185">Enter a valid positive amount${!isDeposit ? " and destination address/UPI ID" : ""}.</span>`;
        }
        return;
      }

      const liveBalObj = this.options.appState?.getLiveBalance?.();
      const rawBal = liveBalObj ? Number(liveBalObj.available_balance || 0) : 0;
      const activeCurr = (liveBalObj?.currency || this.options.appState?.getSelectedCurrency?.() || "USD").toUpperCase();
      const sym = activeCurr === "INR" ? "₹" : "$";

      if (!isDeposit) {
        const entered = parseFloat(amount);
        if (isNaN(entered) || entered > rawBal) {
          if (result) {
            result.innerHTML = `<span style="color:#fb7185">Insufficient balance: Requested amount (${sym}${entered ? entered.toFixed(2) : amount} ${activeCurr}) exceeds your available balance of ${sym}${formatSmartBalance(rawBal)} ${activeCurr}.</span>`;
          }
          updateWithdrawalValidation();
          document.getElementById("cashier-amount")?.focus();
          return;
        }
      }

      try {
        if (result) result.textContent = "Processing secure request…";

        if (isDeposit && network.startsWith("CUSTOM:")) {
          const method = this.customMethods.find((x) => `CUSTOM:${x.setting_key}` === network);
          const ticket = await this.options.restClient.createSupportTicket({
            category: "DEPOSIT",
            priority: "NORMAL",
            subject: `Manual deposit via ${method?.display_name || "custom method"}`,
            message: `Manual deposit review requested. Amount ${amount} ${this.selectedCurrency}; method ${method?.display_name || network}; reference ${String(method?.config?.reference || "not supplied")}.`,
          });
          if (result) {
            result.innerHTML = `<div style="color:#34d399">Request ${this.options.escapeHTML(String(ticket.ticket_id))} opened for operator verification.</div>`;
          }
          return;
        }

        if (isDeposit) {
          // Determine chosen bonus campaign
          const checkedBonusRadio = document.querySelector('input[name="desktop-bonus-campaign"]:checked') as HTMLInputElement | null;
          const selectEl = document.getElementById("cashier-bonus") as HTMLSelectElement | null;
          let rawBonus = "";
          if (checkedBonusRadio) {
            rawBonus = checkedBonusRadio.value.trim();
          } else if (selectEl) {
            rawBonus = selectEl.value.trim();
          } else if (!this.userDeclinedBonus) {
            rawBonus = this.selectedBonusId || "";
          }

          if (rawBonus === "none" || rawBonus === "" || this.userDeclinedBonus) {
            rawBonus = "";
          }

          const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(rawBonus);
          const isCompatible = isUUID && this.getCompatibleBonuses().some((b) => String(b.campaign_id) === rawBonus);
          const bonusCampaign = isCompatible ? rawBonus : undefined;

          let payAmountStr: string;
          let payCurrency: string;
          let targetCurrencyStr: string;
          let targetAmountStr: string;
          let fxRateStr: string;

          const liveRate = getLiveUsdInrRate();
          const enteredNum = parseFloat(amount) || this.selectedAmount;

          const targetCurr = (this.targetAccountCurrency || "USD").toUpperCase();
          const targetFxRate = getLiveUsdFxRate(targetCurr);

          if (this.selectedMethod.category === "CRYPTO") {
            payCurrency = (this.selectedMethod.currency || "USDT").toUpperCase();
            const cryptoPrice = this.cryptoRates[payCurrency] || 1.0;
            let targetAmt: number;
            let usdAmt: number;

            if (this.selectedCurrency === targetCurr) {
              targetAmt = enteredNum;
              usdAmt = targetFxRate > 0 ? targetAmt / targetFxRate : targetAmt;
            } else if (this.selectedCurrency === "USD") {
              usdAmt = enteredNum;
              targetAmt = usdAmt * targetFxRate;
            } else {
              const srcRate = getLiveUsdFxRate(this.selectedCurrency);
              usdAmt = srcRate > 0 ? enteredNum / srcRate : enteredNum;
              targetAmt = usdAmt * targetFxRate;
            }

            const cryptoAmt = (payCurrency === "USDT" || payCurrency === "USDC") ? usdAmt : (usdAmt / cryptoPrice);
            payAmountStr = this.formatCryptoAmount(cryptoAmt, payCurrency);
            targetCurrencyStr = targetCurr;
            targetAmountStr = targetAmt.toFixed(2);
            fxRateStr = (targetFxRate * cryptoPrice).toFixed(6);
          } else {
            // Fiat payment (e.g. UPI for India or local rails)
            payCurrency = targetCurr;
            payAmountStr = enteredNum.toFixed(2);
            targetCurrencyStr = targetCurr;
            targetAmountStr = payAmountStr;
            fxRateStr = "1.000000";
          }

          const depResponse = await this.options.restClient.createDepositIntent(
            network,
            payAmountStr,
            payCurrency,
            bonusCampaign,
            targetCurrencyStr,
            targetAmountStr,
            fxRateStr
          );
          this.currentDepositResponse = depResponse;
          try {
            localStorage.setItem("starx_first_deposit_banner_dismissed", "true");
          } catch {}
          document.getElementById("first-deposit-promo")?.classList.remove("show");
          this.activeStep = 2;
          this.startCountdownTimer();
          this.render();
        } else {
          // Withdrawal
          const isCrypto = network.includes("TRC20") || network.includes("BEP20") || network.includes("BTC") || network.includes("TON") || network.includes("CRYPTO") || network.includes("ETH") || network.includes("USDC");
          const payoutNetwork = isCrypto
            ? (network.includes("BEP20") ? "BEP20" : (network.includes("TON") ? "TON" : (network.includes("BTC") ? "BTC" : (network.includes("ETH") ? "ETH" : (network.includes("USDC") ? "USDC" : "TRC20")))))
            : (destination.includes("@") ? "UPI" : "IMPS");
          const response = await this.options.restClient.requestWithdrawal(
            payoutNetwork,
            amount,
            destination,
            activeCurr
          );
          if (result) {
            result.innerHTML = `<div style="color:#34d399">✓ Withdrawal ${this.options.escapeHTML(String(response.withdrawal_id || response.transaction_id || "submitted"))} · ${this.options.escapeHTML(String(response.status || "PENDING"))}</div>`;
          }

          // Immediately deduct balance in UI and sync across open tabs
          try {
            const amtInput = document.getElementById("cashier-amount") as HTMLInputElement | null;
            if (amtInput) amtInput.value = "";
            const destInput = document.getElementById("cashier-destination") as HTMLInputElement | null;
            if (destInput) destInput.value = "";

            await this.options.appState?.refreshLiveState?.();
            this.options.renderHeader?.();

            this.walletSummary = await this.options.restClient.getWalletSummary(activeCurr).catch(() => null);
            const availEl = document.getElementById("cashier-avail-bal");
            if (availEl && this.walletSummary) {
              availEl.textContent = `${sym}${parseFloat(this.walletSummary.real_available_balance || "0").toFixed(2)}`;
            }

            const bc = new BroadcastChannel("starx_balance_sync");
            bc.postMessage({ type: "BALANCE_UPDATED" });
            setTimeout(() => bc.close(), 100);
          } catch (syncErr) {
            console.warn("[Cashier] Post-withdrawal balance sync error:", syncErr);
          }
        }
      } catch (error: any) {
        if (result) {
          const rawMsg = error?.message || "Cashier request failed";
          const cleanMsg = rawMsg.replace(/^pq:\s*/i, '');
          result.innerHTML = `<span style="color:#fb7185">${this.options.escapeHTML(cleanMsg)}</span>`;
        }
      }
    });
  }

  private startCountdownTimer() {
    if (this.countdownTimerInterval) {
      clearInterval(this.countdownTimerInterval);
    }
    this.countdownSecondsRemaining = 900; // 15 mins
    this.countdownTimerInterval = setInterval(() => {
      this.countdownSecondsRemaining--;
      if (this.countdownSecondsRemaining <= 0) {
        clearInterval(this.countdownTimerInterval);
        this.countdownTimerInterval = null;
      }
      const timerEl = document.getElementById("cashier-countdown");
      if (timerEl) {
        const minutes = Math.floor(this.countdownSecondsRemaining / 60);
        const seconds = this.countdownSecondsRemaining % 60;
        timerEl.textContent = `⏳ ${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      }
    }, 1000);
  }

  private calculateBonusAmount(): number {
    if (this.userDeclinedBonus || !this.selectedBonusId) return 0;
    const usdEquivalent = this.selectedCurrency === "INR" ? this.selectedAmount / getLiveUsdInrRate() : this.selectedAmount;
    const match = this.getCompatibleBonuses().find((b) => String(b.campaign_id) === this.selectedBonusId);
    if (match) {
      return (usdEquivalent * (Number(match.match_percentage_bps || 0) / 10000));
    }
    return 0;
  }

  private getBlockchainExplorer(network: string, address: string): { name: string; url: string } {
    const net = (network || "").toUpperCase();
    const cleanAddr = encodeURIComponent(address || "");

    if (net.includes("TRC20") || net.includes("TRON") || net.includes("TRX")) {
      return {
        name: "Tronscan",
        url: `https://tronscan.org/#/address/${cleanAddr}`,
      };
    }
    if (net.includes("BEP20") || net.includes("BSC") || net.includes("BINANCE")) {
      return {
        name: "BscScan",
        url: `https://bscscan.com/address/${cleanAddr}`,
      };
    }
    if (net.includes("BTC") || net.includes("BITCOIN")) {
      return {
        name: "Blockchain.com",
        url: `https://www.blockchain.com/explorer/addresses/btc/${cleanAddr}`,
      };
    }
    if (net.includes("SOL") || net.includes("SOLANA")) {
      return {
        name: "Solscan",
        url: `https://solscan.io/account/${cleanAddr}`,
      };
    }
    if (net.includes("TON")) {
      return {
        name: "Tonviewer",
        url: `https://tonviewer.com/${cleanAddr}`,
      };
    }
    if (net.includes("POLYGON") || net.includes("MATIC") || net.includes("POL")) {
      return {
        name: "Polygonscan",
        url: `https://polygonscan.com/address/${cleanAddr}`,
      };
    }
    if (net.includes("ARBITRUM") || net.includes("ARB")) {
      return {
        name: "Arbiscan",
        url: `https://arbiscan.io/address/${cleanAddr}`,
      };
    }
    return {
      name: "Etherscan",
      url: `https://etherscan.io/address/${cleanAddr}`,
    };
  }

  private getAllAvailableMethods(): PaymentMethodItem[] {
    const list = [...DEFAULT_METHODS];
    for (const cm of this.customMethods) {
      list.push({
        id: `CUSTOM:${cm.setting_key}`,
        name: cm.display_name || cm.setting_key,
        category: "FIAT",
        currency: "USD",
        badge: "CUSTOM",
        badgeColor: "#8b5cf6",
        speed: "Manual Verification",
        fee: "0% Free",
        minAmount: 10,
        maxAmount: 100000,
        iconSvg: BRAND_ICONS.bank,
        instructions: cm.config?.instructions,
      });
    }
    return list;
  }
}
